import { useCallback, useEffect, useRef, useState } from 'react';
import type { AltYActivityEntry, AltYBridgeContext, AltYChatGroup, AltYChatMessage, AltYChatSummary, AltYDevEnv, AltYEnvironmentTab, AltYImageAttachment, AltYMode, AltYParticipant } from '../types/global';
// chat.status values from the broker. 1 = running, 4 = completed.
const CHAT_STATUS_RUNNING = 1;
const CHAT_STATUS_COMPLETED = 4;
import {
  type AltYTranscriptResponse,
  getAltYTranscript,
  getAltYToolOutput,
  listAltYChats,
  listAltYChatGroups,
  createAltYChatGroup,
  updateAltYChatGroup,
  deleteAltYChatGroup,
  setAltYChatGroup,
  mintAltYSession,
  renameAltYChat,
  setAltYChatRunning,
  deleteAltYChat,
  listAltYParticipants,
  addAltYParticipant,
  addAltYHumanMessage,
  getAuthedEmail,
} from './altYApi';
import { executePageCommand, navigationTargetUrl, pollPendingPageCommand, postPageCommandResult } from './pageCommands';
import { AGENT_PRIMER_SEPARATOR } from './pinnedAgents';
import type { FlowDoc } from '../types/global';

const modeKey = 'altY.mode';
// Last-opened chat is remembered per mode so switching modes restores each
// mode's own chat rather than cross-contaminating.
function lastRunKeyFor(mode: AltYMode) {
  return `altY.lastRunId.${mode}`;
}
const reconnectBaseMs = 1000;
const reconnectMaxMs = 30000;
const MESSAGE_CACHE_LIMIT = 20;
const PERSISTED_CACHE_KEY = 'altY.chatCache.v1';
const PERSISTED_CACHE_VERSION = 1;
const PERSISTED_CACHE_TTL_MS = 14 * 24 * 60 * 60 * 1000;
const PERSISTED_CACHE_MAX_BYTES = 3_500_000;
const PAGE_CONTEXT_KEY = 'altY.chatPageContext.v1';
const PAGE_CONTEXT_VERSION = 1;
const PAGE_CONTEXT_LIMIT = 200;
const ACTIVITY_LIMIT = 200;
const ACTIVITY_DETAIL_LIMIT = 900;
const TRANSIENT_CONNECT_ERROR = /HTTP (502|503|504)|Bad Gateway|Service Unavailable|Gateway Timeout|Failed to fetch|NetworkError|Load failed/i;

type ChatCacheEntry = {
  messages: AltYChatMessage[];
  events: unknown[];
  lineCount: number;
  updatedAt: number;
};

type StoredChatCache = {
  version?: number;
  entries?: Record<string, unknown>;
};

type StoredPageContext = AltYBridgeContext & {
  tabId?: string | null;
  tabLabel?: string | null;
  tabUrl?: string | null;
  capturedAt?: number;
};

type StoredPageContextCache = {
  version?: number;
  entries?: Record<string, unknown>;
};

function readInitialMode(): AltYMode {
  const stored = localStorage.getItem(modeKey);
  if (stored === 'alty-dev' || stored === 'agent-factory' || stored === 'online-dev-mvp' || stored === 'bmad' || stored === 'chat' || stored === 'scope-tasks') return stored;
  return 'interact';
}

type UseAltYChatArgs = {
  isOpen: boolean;
  context: AltYBridgeContext | null;
  appFrame: HTMLIFrameElement | null;
  activeTab?: AltYEnvironmentTab | null;
  frameForBaseUrl?: (baseUrl: string | null) => HTMLIFrameElement | null;
  onCrossServerNavigate?: (url: string) => Promise<CrossServerNavigateResult>;
};

type CrossServerNavigateResult = {
  ok: boolean;
  url?: string;
  baseUrl?: string;
  tab?: { id: string; label: string; url: string } | null;
  error?: string;
};

function originFromUrl(value?: string | null): string | null {
  if (!value) return null;
  try {
    return new URL(value, window.location.href).origin;
  } catch {
    return null;
  }
}

function targetBaseUrlFor(context: AltYBridgeContext | null, tab: AltYEnvironmentTab | null | undefined): string | null {
  return originFromUrl(tab?.url) || context?.baseUrl || context?.origin || originFromUrl(context?.url);
}

function id(prefix: string) {
  return `${prefix}-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object' && !Array.isArray(value);
}

function normalizeCacheEntry(value: unknown, now: number): ChatCacheEntry | null {
  if (!isRecord(value)) return null;
  const messages = Array.isArray(value.messages) ? value.messages as AltYChatMessage[] : [];
  const events = Array.isArray(value.events) ? value.events : [];
  const updatedAt = typeof value.updatedAt === 'number' && Number.isFinite(value.updatedAt)
    ? value.updatedAt
    : 0;
  if (!updatedAt || now - updatedAt > PERSISTED_CACHE_TTL_MS) return null;
  if (!messages.length && !events.length) return null;
  const rawLineCount = typeof value.lineCount === 'number' && Number.isFinite(value.lineCount)
    ? value.lineCount
    : events.length;
  return {
    messages,
    events,
    lineCount: Math.max(0, Math.floor(rawLineCount)),
    updatedAt,
  };
}

function serializeChatCache(entries: Array<[number, ChatCacheEntry]>): string {
  const stored: StoredChatCache = { version: PERSISTED_CACHE_VERSION, entries: {} };
  for (const [runId, entry] of entries) {
    stored.entries![String(runId)] = entry;
  }
  return JSON.stringify(stored);
}

function pruneChatCache(cache: Map<number, ChatCacheEntry>): Array<[number, ChatCacheEntry]> {
  const now = Date.now();
  let entries = Array.from(cache.entries())
    .filter(([, entry]) => now - entry.updatedAt <= PERSISTED_CACHE_TTL_MS)
    .sort((a, b) => b[1].updatedAt - a[1].updatedAt)
    .slice(0, MESSAGE_CACHE_LIMIT);
  while (entries.length && serializeChatCache(entries).length > PERSISTED_CACHE_MAX_BYTES) {
    entries = entries.slice(0, -1);
  }
  return entries;
}

function writePersistedChatCache(cache: Map<number, ChatCacheEntry>): void {
  try {
    let entries = pruneChatCache(cache);
    cache.clear();
    for (const [runId, entry] of entries) cache.set(runId, entry);
    while (true) {
      try {
        localStorage.setItem(PERSISTED_CACHE_KEY, serializeChatCache(entries));
        return;
      } catch {
        if (!entries.length) {
          localStorage.removeItem(PERSISTED_CACHE_KEY);
          return;
        }
        entries = entries.slice(0, -1);
        cache.clear();
        for (const [runId, entry] of entries) cache.set(runId, entry);
      }
    }
  } catch {
    /* localStorage may be unavailable; in-memory cache still works. */
  }
}

function normalizeStoredPageContext(value: unknown): StoredPageContext | null {
  if (!isRecord(value)) return null;
  const url = typeof value.url === 'string' ? value.url : '';
  const origin = typeof value.origin === 'string' ? value.origin : originFromUrl(url);
  const baseUrl = typeof value.baseUrl === 'string' ? value.baseUrl : origin;
  if (!url && !baseUrl) return null;
  const visibleFields = isRecord(value.visibleFields)
    ? { ...(value.visibleFields as Record<string, unknown>) }
    : {};
  const selectedRows = Array.isArray(value.selectedRows)
    ? [...value.selectedRows]
    : typeof value.selectedRows === 'string'
      ? value.selectedRows
      : undefined;
  const selectedDocumentRows = Array.isArray(value.selectedDocumentRows)
    ? (value.selectedDocumentRows as AltYBridgeContext['selectedDocumentRows'])
    : undefined;
  const capturedAt = typeof value.capturedAt === 'number' && Number.isFinite(value.capturedAt)
    ? value.capturedAt
    : Date.now();
  return {
    url: url || undefined,
    origin,
    baseUrl,
    path: typeof value.path === 'string' ? value.path : undefined,
    title: typeof value.title === 'string' ? value.title : undefined,
    documentId: typeof value.documentId === 'string' || typeof value.documentId === 'number' ? value.documentId : null,
    documentTypeId: typeof value.documentTypeId === 'string' || typeof value.documentTypeId === 'number' ? value.documentTypeId : null,
    visibleFields,
    selectedRows,
    selectedDocumentRows,
    customerHost: typeof value.customerHost === 'string' ? value.customerHost : null,
    customerId: typeof value.customerId === 'string' ? value.customerId : null,
    customerName: typeof value.customerName === 'string' ? value.customerName : null,
    dirty: typeof value.dirty === 'boolean' ? value.dirty : undefined,
    tabId: typeof value.tabId === 'string' ? value.tabId : null,
    tabLabel: typeof value.tabLabel === 'string' ? value.tabLabel : null,
    tabUrl: typeof value.tabUrl === 'string' ? value.tabUrl : null,
    capturedAt,
  };
}

function snapshotPageContext(
  context: AltYBridgeContext | StoredPageContext | null,
  tab: AltYEnvironmentTab | null | undefined,
): StoredPageContext | null {
  if (!context && !tab) return null;
  const stored = context as StoredPageContext | null;
  const url = context?.url || tab?.url || '';
  const origin = context?.origin || originFromUrl(url) || originFromUrl(tab?.url);
  const baseUrl = context?.baseUrl || origin || originFromUrl(tab?.url);
  if (!url && !baseUrl) return null;
  return {
    url: url || undefined,
    origin,
    baseUrl,
    path: context?.path,
    title: context?.title,
    documentId: context?.documentId ?? null,
    documentTypeId: context?.documentTypeId ?? null,
    visibleFields: context?.visibleFields ? { ...context.visibleFields } : {},
    selectedRows: Array.isArray(context?.selectedRows)
      ? [...context.selectedRows]
      : context?.selectedRows,
    selectedDocumentRows: Array.isArray(context?.selectedDocumentRows)
      ? context.selectedDocumentRows.map((group) => ({
          grid: group.grid,
          key: group.key ?? null,
          rows: group.rows.map((row) => ({ ...row })),
        }))
      : undefined,
    customerHost: context?.customerHost ?? null,
    customerId: context?.customerId ?? null,
    customerName: context?.customerName ?? null,
    dirty: context?.dirty,
    tabId: tab?.id ?? stored?.tabId ?? null,
    tabLabel: tab?.label ?? stored?.tabLabel ?? null,
    tabUrl: tab?.url ?? stored?.tabUrl ?? null,
    capturedAt: Date.now(),
  };
}

function readPersistedPageContexts(): Map<number, StoredPageContext> {
  const map = new Map<number, StoredPageContext>();
  try {
    const raw = localStorage.getItem(PAGE_CONTEXT_KEY);
    if (!raw) return map;
    const parsed = JSON.parse(raw) as StoredPageContextCache;
    if (!parsed || typeof parsed !== 'object' || !isRecord(parsed.entries)) return map;
    for (const [key, value] of Object.entries(parsed.entries)) {
      const runId = Number(key);
      if (!Number.isFinite(runId)) continue;
      const normalized = normalizeStoredPageContext(value);
      if (normalized) map.set(runId, normalized);
    }
  } catch {
    /* ignore corrupt localStorage */
  }
  return map;
}

function writePersistedPageContexts(cache: Map<number, StoredPageContext>): void {
  try {
    const entries = Array.from(cache.entries())
      .filter(([, entry]) => !!entry.url || !!entry.baseUrl)
      .sort((a, b) => (b[1].capturedAt || 0) - (a[1].capturedAt || 0))
      .slice(0, PAGE_CONTEXT_LIMIT);
    cache.clear();
    const payload: StoredPageContextCache = { version: PAGE_CONTEXT_VERSION, entries: {} };
    for (const [runId, entry] of entries) {
      cache.set(runId, entry);
      payload.entries![String(runId)] = entry;
    }
    localStorage.setItem(PAGE_CONTEXT_KEY, JSON.stringify(payload));
  } catch {
    /* ignore quota/serialization failures */
  }
}

function rememberPageContextForRun(
  cache: Map<number, StoredPageContext>,
  runId: number | null | undefined,
  context: AltYBridgeContext | StoredPageContext | null,
  tab: AltYEnvironmentTab | null | undefined,
): StoredPageContext | null {
  if (runId == null) return null;
  const snapshot = snapshotPageContext(context, tab);
  if (!snapshot) return null;
  cache.set(runId, snapshot);
  writePersistedPageContexts(cache);
  return snapshot;
}

function tabPayloadForContext(
  context: StoredPageContext | AltYBridgeContext | null,
  tab: AltYEnvironmentTab | null | undefined,
) {
  const stored = context as StoredPageContext | null;
  if (stored?.tabId || stored?.tabLabel || stored?.tabUrl) {
    return {
      id: stored.tabId || tab?.id || null,
      label: stored.tabLabel || tab?.label || null,
      url: stored.tabUrl || tab?.url || null,
    };
  }
  return tab ? { id: tab.id, label: tab.label, url: tab.url } : null;
}

function readPersistedChatCache(): Map<number, ChatCacheEntry> {
  const cache = new Map<number, ChatCacheEntry>();
  try {
    const raw = localStorage.getItem(PERSISTED_CACHE_KEY);
    if (!raw) return cache;
    const stored = JSON.parse(raw) as unknown;
    if (!isRecord(stored)) return cache;
    const typed = stored as StoredChatCache;
    if (typed.version !== PERSISTED_CACHE_VERSION || !isRecord(typed.entries)) return cache;
    const now = Date.now();
    for (const [key, value] of Object.entries(typed.entries)) {
      const runId = Number(key);
      if (!Number.isFinite(runId)) continue;
      const entry = normalizeCacheEntry(value, now);
      if (entry) cache.set(runId, entry);
    }
    writePersistedChatCache(cache);
  } catch {
    try {
      localStorage.removeItem(PERSISTED_CACHE_KEY);
    } catch {
      /* ignore */
    }
  }
  return cache;
}

function clearPersistedChatCache(): void {
  try {
    localStorage.removeItem(PERSISTED_CACHE_KEY);
  } catch {
    /* ignore */
  }
}

function mergeTranscriptEvents(cached: ChatCacheEntry | undefined, transcript: AltYTranscriptResponse) {
  const fetchedEvents = Array.isArray(transcript.events) ? transcript.events : [];
  const responseLineCount = typeof transcript.lineCount === 'number' && Number.isFinite(transcript.lineCount)
    ? Math.max(0, Math.floor(transcript.lineCount))
    : null;
  const fromLine = typeof transcript.fromLine === 'number' && Number.isFinite(transcript.fromLine)
    ? Math.max(0, Math.floor(transcript.fromLine))
    : null;
  const canAppend = !!cached && cached.events.length > 0 && responseLineCount !== null && fromLine === cached.lineCount;
  const events = canAppend ? [...cached.events, ...fetchedEvents] : fetchedEvents;
  return {
    events,
    lineCount: responseLineCount ?? events.length,
  };
}

function tryParseJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

// navigator.clipboard is undefined outside secure contexts (plain HTTP), so
// fall back to a hidden textarea + execCommand when it isn't available.
async function writeToClipboard(text: string): Promise<void> {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }
  const textarea = document.createElement('textarea');
  textarea.value = text;
  textarea.style.position = 'fixed';
  textarea.style.opacity = '0';
  document.body.appendChild(textarea);
  textarea.select();
  try {
    if (!document.execCommand('copy')) throw new Error('copy command rejected');
  } finally {
    document.body.removeChild(textarea);
  }
}

function formatToolValue(value: unknown): string {
  if (typeof value === 'string') {
    const parsed = tryParseJson(value);
    if (parsed !== null) return formatToolValue(parsed);
    return value;
  }

  if (Array.isArray(value)) {
    if (value.every((item) => item && typeof item === 'object' && (item as Record<string, unknown>).type === 'text')) {
      return value.map((item) => formatToolValue((item as Record<string, unknown>).text || '')).join('\n');
    }
    return JSON.stringify(value, null, 2);
  }

  if (value && typeof value === 'object') {
    return JSON.stringify(value, null, 2);
  }

  return String(value ?? '');
}

// A collapsed tool card should read as the *call* it made — the file, command
// or query — not a fragment of its output. Pull the salient input field.
function summarizeToolInput(input: string): string {
  const parsed = tryParseJson(input);
  if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
    const obj = parsed as Record<string, unknown>;
    for (const key of ['command', 'file_path', 'path', 'pattern', 'query', 'url', 'description', 'prompt']) {
      const value = obj[key];
      if (typeof value !== 'string' || !value.trim()) continue;
      const clean = value.replace(/\s+/g, ' ').trim();
      // For paths the tail is what tells one call apart from the next — the
      // shared leading directories just make every row look identical.
      const display = (key === 'file_path' || key === 'path') && clean.includes('/')
        ? clean.split('/').slice(-2).join('/')
        : clean;
      return display.slice(0, 140);
    }
  }
  return input.replace(/\s+/g, ' ').trim().slice(0, 140);
}

// How much of each tool's output to keep when copying the chat. Whole tool
// outputs (a full file read, a long command dump) are what made the old
// "copy the raw transcript" so unwieldy — a short slice is enough to read what
// the call returned.
const TOOL_OUTPUT_COPY_LIMIT = 600;

function truncateForCopy(text: string, limit: number): string {
  const trimmed = text.trim();
  if (trimmed.length <= limit) return trimmed;
  return `${trimmed.slice(0, limit).trimEnd()}\n… (truncated)`;
}

function truncateActivityText(value: unknown, limit = ACTIVITY_DETAIL_LIMIT): string {
  const text = typeof value === 'string' ? value : value == null ? '' : formatToolValue(value);
  const trimmed = text.trim();
  if (trimmed.length <= limit) return trimmed;
  return `${trimmed.slice(0, limit).trimEnd()}...`;
}

function normalizeActivityLevel(value: unknown): AltYActivityEntry['level'] {
  return value === 'tool' || value === 'done' || value === 'error' || value === 'info'
    ? value
    : 'info';
}

// The "Copy chat" button used to dump the full raw JSONL transcript — every
// tool input and every byte of every tool output — which for a real agent turn
// is far more text than anyone wants pasted into a chat or doc. Build a compact,
// readable transcript instead: user/assistant prose in full, but each tool call
// reduced to the call it made plus a short slice of its output, mirroring the
// collapsed tool card the user already sees.
function buildCondensedTranscript(messages: AltYChatMessage[]): string {
  const blocks: string[] = [];
  for (const message of messages) {
    switch (message.role) {
      case 'user': {
        const imageNote = message.images?.length ? ` [${message.images.length} image(s)]` : '';
        const text = message.text.trim();
        if (text || imageNote) blocks.push(`## You${imageNote}\n${text}`.trimEnd());
        break;
      }
      case 'assistant': {
        const text = message.text.trim();
        if (text) blocks.push(`## Alt-Y\n${text}`);
        break;
      }
      case 'tool': {
        const call = (message.toolSummary || summarizeToolInput(message.toolInput || '')).trim();
        const header = `🔧 ${message.toolName || 'tool'}${call ? `: ${call}` : ''}`;
        const output = truncateForCopy(message.toolOutput || '', TOOL_OUTPUT_COPY_LIMIT);
        blocks.push(output ? `${header}\n${output}` : header);
        break;
      }
      case 'meta':
      case 'system': {
        const text = message.text.trim();
        if (text) blocks.push(`_${text}_`);
        break;
      }
    }
  }
  return blocks.join('\n\n');
}

// A chat's latest turn is complete when a `_done` event appears after the last
// `_user` event. We can't just look at the final event: an alty-dev turn that
// modified the broker source appends a `_broker_restart_request` AFTER its
// `_done`, so a strict last-event check left those chats amber in the sidebar
// until the user clicked in (the transcript replay reconciles correctly via
// `replayLastWasDoneRef`). Comparing the last `_done` against the last `_user`
// mirrors that replay behaviour and tolerates any trailing post-turn events.
function transcriptTurnComplete(events: Array<{ type?: string }>): boolean {
  let lastUser = -1;
  let lastDone = -1;
  for (let i = 0; i < events.length; i += 1) {
    const type = events[i]?.type;
    if (type === '_user') lastUser = i;
    else if (type === '_done') lastDone = i;
  }
  return lastDone > lastUser;
}

// A chat the user "started but did nothing with" — they never sent a message
// and the agent never produced one. Only meta/system noise (a "model:" line, a
// connect error) or nothing at all doesn't count as real content. Such a chat
// is safe to discard rather than persist as a permanent sidebar row.
function chatHasContent(messages: AltYChatMessage[]): boolean {
  return messages.some((m) => m.role === 'user' || m.role === 'assistant');
}

function connectErrorMessage(error: unknown): string {
  const message = error instanceof Error ? error.message : String(error);
  if (/HTTP 502|Bad Gateway/i.test(message)) return 'HTTP 502 Bad Gateway';
  if (/HTTP 503|Service Unavailable/i.test(message)) return 'HTTP 503 Service Unavailable';
  if (/HTTP 504|Gateway Timeout/i.test(message)) return 'HTTP 504 Gateway Timeout';
  return message;
}

function isTransientConnectError(error: unknown): boolean {
  return TRANSIENT_CONNECT_ERROR.test(connectErrorMessage(error));
}

export type AltYThinking = {
  startedAt: number;
  outputTokens: number;
  activeTool: string | null;
};

// Extra per-turn knobs that ride alongside `effort` on op:send. `codexEffort`
// is codex's thinking level (effort itself carries the "codex" routing token);
// `fast` is the high-speed/higher-cost mode for claude/codex. `models` (when
// length >= 2) flips the turn into a consensus run: the broker spawns one
// subchat per entry and the main chat shows the synthesised verdict.
export type ConsensusModel = { effort: string; codexEffort?: string; fast?: boolean; max?: boolean; label?: string };
export type SendExtra = { codexEffort?: string; fast?: boolean; max?: boolean; models?: ConsensusModel[] };

// A message the user queued while a turn was running, with everything needed to
// dispatch it verbatim once the turn finishes. The `id` keys the pending bubble
// + its cancel button.
export type QueuedSend = { id: string; text: string; images: AltYImageAttachment[]; effort: string; extra: SendExtra };

// Backs the composer's "next turn ≈ N tok • $X" chip. Pushed by the broker
// as `_prefix_estimate` after every `_done`; the panel also fetches it on
// chat-switch / model-change. `null` = not yet known (chip falls back to
// HTTP fetch).
export type AltYPrefixEstimate = {
  tokens: number;
  bytes: number;
  model: string;
  input_price_per_mtok: number;
  cache_read_price_per_mtok?: number;
  estimated_cost_usd: number;
  last_turn_cache_hit_ratio: number | null;
  breakdown: {
    system_tokens: number;
    summaries_tokens: number;
    verbatim_tokens: number;
  };
};

export function useAltYChat({
  isOpen,
  context,
  appFrame,
  activeTab = null,
  frameForBaseUrl,
  onCrossServerNavigate,
}: UseAltYChatArgs) {
  const [chats, setChats] = useState<AltYChatSummary[]>([]);
  // Owner-scoped sidebar folders. Independently loaded from /chat-groups so a
  // group can exist even when its set of chats is empty; mutation goes through
  // the createGroup/renameGroup/deleteGroup/setChatGroup helpers below.
  const [chatGroups, setChatGroups] = useState<AltYChatGroup[]>([]);
  const [messages, setMessages] = useState<AltYChatMessage[]>([]);
  const [activities, setActivities] = useState<AltYActivityEntry[]>([]);
  const [status, setStatus] = useState('offline');
  const [busy, setBusy] = useState(false);
  const [connected, setConnected] = useState(false);
  const [activeRunId, setActiveRunId] = useState<number | null>(null);
  const [sidebarExpanded, setSidebarExpanded] = useState(true);
  const [mode, setMode] = useState<AltYMode>(readInitialMode);
  const [thinking, setThinking] = useState<AltYThinking | null>(null);
  // The ACTIVE chat's queued messages — submitted while a turn was still
  // running, surfaced as dimmed pending bubbles at the bottom of the chat (not
  // just a faint meta line) and dispatched in order (FIFO) as each turn
  // finishes; the user can cancel any of them. A LIST rather than one slot so
  // firing several messages in a row while busy doesn't overwrite all but the
  // last; kept PER chat (see queuedByRunIdRef) so switching chats doesn't lose
  // them.
  const [queuedMessages, setQueuedMessages] = useState<QueuedSend[]>([]);
  // Last `_dev_env` event for the current chat. Online Dev MVP mode emits this
  // once the chat's own online21 dev fork is up; the panel renders an iframe
  // on the left of the screen pointing at `url`. Reset on chat switch (the
  // attached chat's transcript-replay re-populates it if present).
  const [devEnv, setDevEnv] = useState<AltYDevEnv | null>(null);
  const [participants, setParticipants] = useState<AltYParticipant[]>([]);
  const [currentUserEmail, setCurrentUserEmail] = useState<string | null>(null);
  const currentUserEmailRef = useRef<string | null>(null);
  currentUserEmailRef.current = currentUserEmail;
  // Pushed by the broker right after each turn finishes — the panel uses
  // it to refresh the composer's cost chip without a re-fetch. Reset on
  // chat switch (the chip's own effect refetches for the new chat id).
  const [prefixEstimate, setPrefixEstimate] = useState<AltYPrefixEstimate | null>(null);
  // Anthropic's `message_delta.usage.output_tokens` is cumulative *per message*,
  // and a single turn fires many such messages. Keep a committed total of
  // finished messages and the in-flight count of the current one, so the
  // displayed total stays monotonic across the whole turn.
  const tokensRef = useRef({ committed: 0, inFlight: 0 });
  const runningToolNameRef = useRef<string | null>(null);

  const wsRef = useRef<WebSocket | null>(null);
  const modeRef = useRef<AltYMode>(mode);
  const chatsRef = useRef<AltYChatSummary[]>([]);
  const activeRunIdRef = useRef<number | null>(null);
  const activeAssistantIdRef = useRef<string | null>(null);
  // Anthropic's stream delivers text in uneven bursts. We buffer incoming
  // deltas per assistant-message and drain them on a rAF tick at a steady
  // rate, so the chat types out smoothly instead of lurching.
  const pendingTextRef = useRef<Map<string, string>>(new Map());
  const rafRef = useRef<number | null>(null);
  const toolMessageByUseIdRef = useRef<Record<string, string>>({});
  const toolNameByUseIdRef = useRef<Record<string, string>>({});
  const latestToolUseIdRef = useRef<string | null>(null);
  const openedOnceRef = useRef(false);
  const deliberateCloseRef = useRef(false);
  const reconnectTimerRef = useRef<number | null>(null);
  const reconnectAttemptRef = useRef(0);
  const connectFailureNoticeRunIdRef = useRef<number | null>(null);
  // Pending liveness-probe timeout. When the page comes back to the foreground
  // we ping the socket and arm this; any inbound frame clears it, otherwise the
  // socket is treated as a zombie (still readyState OPEN but actually dead — the
  // common mobile case after the OS suspends a backgrounded tab) and we force a
  // reconnect.
  const livenessTimerRef = useRef<number | null>(null);
  // Bumped on every connectFlow so a superseded switch (rapid clicking
  // through several chats) bails out instead of clobbering the chat the user
  // actually landed on. Without this, a slow mint from the first click would
  // overwrite the messages of the chat opened by the second click when it
  // finally resolved.
  const connectSeqRef = useRef(0);
  const contextRef = useRef(context);
  const appFrameRef = useRef(appFrame);
  const activeTabRef = useRef(activeTab);
  const frameForBaseUrlRef = useRef(frameForBaseUrl);
  const crossServerNavigateRef = useRef(onCrossServerNavigate);
  const pageCommandSessionIdRef = useRef<string | null>(null);
  const pageCommandBaseUrlRef = useRef<string | null>(null);
  const allowAutoRenameRef = useRef(false);
  const userSentRef = useRef(false);
  const cancelOnAttachRef = useRef(false);
  const replayingRef = useRef(false);
  const replayMessagesRef = useRef<AltYChatMessage[] | null>(null);
  const replayActivitiesRef = useRef<AltYActivityEntry[] | null>(null);
  // Per-runId snapshots. The transcript cache is persisted to localStorage so
  // mobile reloads/switches paint immediately, then reconcile from the server
  // by asking only for transcript lines after the cached lineCount.
  const transcriptCacheRef = useRef<Map<number, ChatCacheEntry>>(readPersistedChatCache());
  const pageContextByRunIdRef = useRef<Map<number, StoredPageContext>>(readPersistedPageContexts());
  const messageCacheRef = useRef<Map<number, AltYChatMessage[]>>(
    new Map(
      Array.from(transcriptCacheRef.current.entries())
        .filter(([, entry]) => entry.messages.length)
        .map(([runId, entry]) => [runId, entry.messages] as [number, AltYChatMessage[]]),
    ),
  );
  const preservedEmptyRunIdsRef = useRef<Set<number>>(new Set());
  const messagesRef = useRef<AltYChatMessage[]>([]);
  const persistCacheTimerRef = useRef<number | null>(null);
  // While replaying a transcript we don't push status updates to the server, so
  // we'd otherwise miss the case where a chat finished while we were detached.
  // Track whether the replay ends with the chat in a "done" state so we can
  // reconcile the server's running flag once replay completes.
  const replayLastWasDoneRef = useRef(false);
  // Queued messages are kept PER chat (keyed by runId), not in one shared slot,
  // so switching to another chat and back doesn't lose what you queued — each
  // chat's queue dispatches when that chat's own turn finishes. `queuedMessages`
  // (state, above) mirrors the ACTIVE chat's queue for rendering.
  const queuedByRunIdRef = useRef<Map<number, QueuedSend[]>>(new Map());
  const sendMessageRef = useRef<((text: string, effort?: string, images?: AltYImageAttachment[], extra?: SendExtra, fromQueue?: boolean) => void) | null>(null);
  const attachChatRef = useRef<((runId: number) => Promise<number | null>) | null>(null);
  // One-shot system primer prepended to the next user message — set by App
  // when the user activates a pinned agent so the agent's flow becomes the
  // instructions for the chat. Cleared after the first send.
  const pendingPrimerRef = useRef<string | null>(null);
  // One-shot AUTO flag for the next send — set by App when the AUTO agent is
  // active. The broker wraps that turn with AUTO_PROMPT (all agents' directions
  // inline + self-route) instead of doing a separate routing call. Consumed
  // alongside the primer in sendMessage. Like the primer it's read only on the
  // non-busy path, so a turn queued while busy keeps the flag for its replay.
  const pendingAutoRef = useRef(false);
  // Wall-clock of the last completed listAltYChats response — used to skip the
  // post-attach refresh when the sidebar was already pulled within the last
  // couple of seconds (rapid switches between chats would otherwise fire a
  // chat-list HTTP + arrangeChatTree/Map rebuild on every click).
  const lastChatsRefreshAtRef = useRef(0);

  const flushPersistedChatCache = useCallback(() => {
    if (persistCacheTimerRef.current != null) {
      window.clearTimeout(persistCacheTimerRef.current);
      persistCacheTimerRef.current = null;
    }
    writePersistedChatCache(transcriptCacheRef.current);
  }, []);

  const schedulePersistedChatCache = useCallback(() => {
    if (persistCacheTimerRef.current != null) return;
    // Long debounce: each write serialises every cached transcript (up to a few
    // MB) on the main thread, so during a live stream we want it to fire ~once
    // every few seconds, not on every rAF drain. pagehide/visibilitychange
    // (above) flush synchronously so unloading the tab never loses recent
    // events.
    persistCacheTimerRef.current = window.setTimeout(() => {
      persistCacheTimerRef.current = null;
      writePersistedChatCache(transcriptCacheRef.current);
    }, 3000);
  }, []);

  useEffect(() => {
    void getAuthedEmail().then((email) => {
      if (email) {
        setCurrentUserEmail(email);
      }
    });
  }, []);

  useEffect(() => {
    contextRef.current = context;
    if (context) {
      rememberPageContextForRun(
        pageContextByRunIdRef.current,
        activeRunIdRef.current,
        context,
        activeTabRef.current,
      );
    }
  }, [context]);

  useEffect(() => {
    appFrameRef.current = appFrame;
  }, [appFrame]);

  useEffect(() => {
    activeTabRef.current = activeTab;
  }, [activeTab]);

  useEffect(() => {
    frameForBaseUrlRef.current = frameForBaseUrl;
  }, [frameForBaseUrl]);

  useEffect(() => {
    crossServerNavigateRef.current = onCrossServerNavigate;
  }, [onCrossServerNavigate]);

  useEffect(() => {
    chatsRef.current = chats;
  }, [chats]);

  useEffect(() => {
    messagesRef.current = messages;
    const runId = activeRunIdRef.current;
    if (runId == null || !chatHasContent(messages)) return;
    const entry = transcriptCacheRef.current.get(runId);
    if (!entry) return;
    transcriptCacheRef.current.delete(runId);
    transcriptCacheRef.current.set(runId, { ...entry, messages, updatedAt: Date.now() });
    schedulePersistedChatCache();
  }, [messages, schedulePersistedChatCache]);

  useEffect(() => {
    const flush = () => flushPersistedChatCache();
    const flushIfHidden = () => {
      if (document.visibilityState === 'hidden') flushPersistedChatCache();
    };
    window.addEventListener('pagehide', flush);
    document.addEventListener('visibilitychange', flushIfHidden);
    return () => {
      window.removeEventListener('pagehide', flush);
      document.removeEventListener('visibilitychange', flushIfHidden);
      flushPersistedChatCache();
    };
  }, [flushPersistedChatCache]);

  const recordLiveTranscriptEvent = useCallback((event: unknown) => {
    if (replayingRef.current) return;
    if (isRecord(event) && event.type === '_chat_renamed') return;
    const runId = activeRunIdRef.current;
    if (runId == null) return;
    const existing = transcriptCacheRef.current.get(runId);
    const next: ChatCacheEntry = {
      events: [...(existing?.events || []), event],
      lineCount: (existing?.lineCount || 0) + 1,
      messages: existing?.messages || messagesRef.current,
      updatedAt: Date.now(),
    };
    transcriptCacheRef.current.delete(runId);
    transcriptCacheRef.current.set(runId, next);
    schedulePersistedChatCache();
  }, [schedulePersistedChatCache]);

  const cacheMessages = useCallback((runId: number, snapshot: AltYChatMessage[]) => {
    if (!snapshot.length) return;
    const cache = messageCacheRef.current;
    // Re-insert so this runId becomes the most-recently-used entry; once we
    // pass the LRU cap, oldest insertion drops out.
    cache.delete(runId);
    cache.set(runId, snapshot);
    while (cache.size > MESSAGE_CACHE_LIMIT) {
      const oldest = cache.keys().next().value;
      if (oldest === undefined) break;
      cache.delete(oldest);
    }
    const transcriptEntry = transcriptCacheRef.current.get(runId);
    if (transcriptEntry) {
      transcriptCacheRef.current.delete(runId);
      transcriptCacheRef.current.set(runId, { ...transcriptEntry, messages: snapshot, updatedAt: Date.now() });
      flushPersistedChatCache();
    }
  }, [flushPersistedChatCache]);

  const cacheTranscript = useCallback((runId: number, events: unknown[], lineCount: number, snapshot: AltYChatMessage[]) => {
    if (!events.length && !snapshot.length) return;
    transcriptCacheRef.current.delete(runId);
    transcriptCacheRef.current.set(runId, {
      events,
      lineCount: Math.max(0, Math.floor(lineCount)),
      messages: snapshot,
      updatedAt: Date.now(),
    });
    flushPersistedChatCache();
    if (snapshot.length) cacheMessages(runId, snapshot);
  }, [cacheMessages, flushPersistedChatCache]);

  // Discard a chat the user opened but never actually used, so abandoning a
  // freshly-started chat doesn't leave an empty placeholder cluttering the
  // sidebar history. Optimistically drops it from the visible list + the local
  // caches and tells the broker to delete the (content-free) row; failure is
  // non-fatal — the row is empty either way.
  const reapEmptyChat = useCallback((runId: number) => {
    if (preservedEmptyRunIdsRef.current.has(runId)) return;
    messageCacheRef.current.delete(runId);
    transcriptCacheRef.current.delete(runId);
    writePersistedChatCache(transcriptCacheRef.current);
    queuedByRunIdRef.current.delete(runId);
    setChats((list) => list.filter((c) => c.runId !== runId));
    void deleteAltYChat(runId).catch(() => undefined);
  }, []);

  const preserveEmptyChat = useCallback((runId: number) => {
    preservedEmptyRunIdsRef.current.add(runId);
  }, []);

  const addMessage = useCallback((message: Omit<AltYChatMessage, 'id'>) => {
    const next = { ...message, id: id(message.role) };
    if (replayMessagesRef.current) {
      replayMessagesRef.current.push(next);
    } else {
      setMessages((items) => [...items, next]);
    }
    return next.id;
  }, []);

  const addMeta = useCallback((text: string) => {
    addMessage({ role: 'meta', text });
  }, [addMessage]);

  const addActivity = useCallback((partial: Omit<AltYActivityEntry, 'id' | 'ts'> & Partial<Pick<AltYActivityEntry, 'id' | 'ts'>>) => {
    const label = String(partial.label || '').trim();
    if (!label) return;
    const entry: AltYActivityEntry = {
      id: partial.id || id('activity'),
      ts: typeof partial.ts === 'number' && Number.isFinite(partial.ts) ? partial.ts : Date.now(),
      provider: partial.provider,
      phase: partial.phase,
      level: normalizeActivityLevel(partial.level),
      label,
      detail: truncateActivityText(partial.detail),
    };
    if (replayActivitiesRef.current) {
      replayActivitiesRef.current.push(entry);
    } else {
      setActivities((items) => [...items, entry].slice(-ACTIVITY_LIMIT));
    }
  }, []);

  const clearChatCache = useCallback(() => {
    messageCacheRef.current.clear();
    transcriptCacheRef.current.clear();
    clearPersistedChatCache();
    addMeta('chat cache cleared');
  }, [addMeta]);

  const addToolMessage = useCallback((toolUseId: string, toolName: string) => {
    const messageId = addMessage({
      role: 'tool',
      text: '',
      toolName,
      toolInput: '',
      toolOutput: '',
      toolStatus: 'running',
      toolSummary: '',
      toolUseId,
    });
    toolMessageByUseIdRef.current[toolUseId] = messageId;
    toolNameByUseIdRef.current[toolUseId] = toolName;
    latestToolUseIdRef.current = toolUseId;
    return messageId;
  }, [addMessage]);

  const updateToolMessage = useCallback((toolUseId: string | null, patch: Partial<AltYChatMessage>) => {
    const effectiveToolUseId = toolUseId || latestToolUseIdRef.current;
    const messageId = effectiveToolUseId ? toolMessageByUseIdRef.current[effectiveToolUseId] : null;
    if (!messageId) return;
    if (replayMessagesRef.current) {
      const idx = replayMessagesRef.current.findIndex((item) => item.id === messageId);
      if (idx !== -1) {
        replayMessagesRef.current[idx] = { ...replayMessagesRef.current[idx], ...patch };
      }
    } else {
      setMessages((items) => items.map((item) => (item.id === messageId ? { ...item, ...patch } : item)));
    }
  }, []);

  const ensureAssistant = useCallback(() => {
    let messageId = activeAssistantIdRef.current;
    if (messageId) return messageId;
    messageId = addMessage({ role: 'assistant', text: '' });
    activeAssistantIdRef.current = messageId;
    return messageId;
  }, [addMessage]);

  const flushPending = useCallback(() => {
    rafRef.current = null;
    const pending = pendingTextRef.current;
    if (pending.size === 0) return;
    const updates = new Map<string, string>();
    for (const [messageId, buffer] of pending) {
      // Proportional drain: bigger backlog → bigger chunks, so we never lag
      // more than ~300ms behind whatever the server has already sent.
      const take = Math.max(1, Math.ceil(buffer.length / 18));
      updates.set(messageId, buffer.slice(0, take));
      const rest = buffer.slice(take);
      if (rest) pending.set(messageId, rest);
      else pending.delete(messageId);
    }
    setMessages((items) => items.map((item) => {
      const chunk = updates.get(item.id);
      return chunk ? { ...item, text: item.text + chunk } : item;
    }));
    if (pending.size > 0) {
      rafRef.current = window.requestAnimationFrame(flushPending);
    }
  }, []);

  const flushAllPending = useCallback(() => {
    if (rafRef.current != null) {
      window.cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    const pending = pendingTextRef.current;
    if (pending.size === 0) return;
    const snapshot = new Map(pending);
    pending.clear();
    setMessages((items) => items.map((item) => {
      const chunk = snapshot.get(item.id);
      return chunk ? { ...item, text: item.text + chunk } : item;
    }));
  }, []);

  const appendAssistantText = useCallback((delta: string) => {
    if (!delta) return;
    const messageId = ensureAssistant();
    // Replaying a stored transcript: skip the typewriter, dump immediately.
    if (replayingRef.current) {
      if (replayMessagesRef.current) {
        const idx = replayMessagesRef.current.findIndex((item) => item.id === messageId);
        if (idx !== -1) {
          replayMessagesRef.current[idx] = {
            ...replayMessagesRef.current[idx],
            text: replayMessagesRef.current[idx].text + delta,
          };
        }
      } else {
        setMessages((items) =>
          items.map((item) => (item.id === messageId ? { ...item, text: item.text + delta } : item)),
        );
      }
      return;
    }
    const prev = pendingTextRef.current.get(messageId) || '';
    pendingTextRef.current.set(messageId, prev + delta);
    if (rafRef.current == null) {
      rafRef.current = window.requestAnimationFrame(flushPending);
    }
  }, [ensureAssistant, flushPending]);

  const refreshChats = useCallback(async () => {
    try {
      setChats(await listAltYChats());
      lastChatsRefreshAtRef.current = Date.now();
    } catch (error) {
      addMeta(`chat list failed: ${(error as Error).message || error}`);
    }
  }, [addMeta]);

  const closeSocket = useCallback(() => {
    if (reconnectTimerRef.current) {
      window.clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
    if (livenessTimerRef.current) {
      window.clearTimeout(livenessTimerRef.current);
      livenessTimerRef.current = null;
    }
    if (rafRef.current != null) {
      window.cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    pendingTextRef.current.clear();
    setBusy(false);
    const ws = wsRef.current;
    if (!ws) return;
    deliberateCloseRef.current = true;
    wsRef.current = null;
    try {
      ws.close();
    } catch {
      /* ignore */
    }
    setConnected(false);
    setStatus('offline');
    setThinking(null);
  }, []);

  const handleEvent = useCallback((event: any) => {
    if (!event || !event.type) return;

    switch (event.type) {
      case '_activity': {
        const rawTs = typeof event.ts === 'number' && Number.isFinite(event.ts) ? event.ts : Date.now();
        addActivity({
          provider: typeof event.provider === 'string' ? event.provider : undefined,
          phase: typeof event.phase === 'string' ? event.phase : undefined,
          level: normalizeActivityLevel(event.level),
          label: typeof event.label === 'string' ? event.label : 'Activity',
          detail: event.detail,
          ts: rawTs < 1_000_000_000_000 ? rawTs * 1000 : rawTs,
        });
        return;
      }
      case '_user': {
        flushAllPending();
        // Pinned-agent invocations send the flow as a primer in front of the
        // actual user request; strip it back out so the bubble shows only what
        // the user typed. The separator is unique enough not to collide with
        // normal text. If a primer was present, surface it as a meta line so
        // the user can still tell which agent was activated.
        const rawText = event.text || '';
        const separatorIndex = rawText.indexOf(AGENT_PRIMER_SEPARATOR);
        const userText = separatorIndex >= 0
          ? rawText.slice(separatorIndex + AGENT_PRIMER_SEPARATOR.length)
          : rawText;
        if (separatorIndex >= 0) {
          const primer = rawText.slice(0, separatorIndex);
          const firstLine = primer.split('\n', 1)[0];
          if (firstLine) addMeta(firstLine);
        }
        addMessage({
          role: 'user',
          text: userText,
          images: Array.isArray(event.images) && event.images.length ? event.images : undefined,
        });
        activeAssistantIdRef.current = null;
        toolMessageByUseIdRef.current = {};
        toolNameByUseIdRef.current = {};
        latestToolUseIdRef.current = null;
        setBusy(true);
        tokensRef.current = { committed: 0, inFlight: 0 };
        runningToolNameRef.current = null;
        if (!replayingRef.current) {
          setThinking({ startedAt: Date.now(), outputTokens: 0, activeTool: null });
        }
        if (replayingRef.current) replayLastWasDoneRef.current = false;
        else if (activeRunIdRef.current) setAltYChatRunning(activeRunIdRef.current, true);
        return;
      }
      case '_human': {
        flushAllPending();
        const rawText = event.text || '';
        const sender = event.senderLabel || event.senderId || 'User';
        const recipient = event.recipientLabel || event.recipientId || '';
        const displayLabel = recipient ? `${sender} to ${recipient}` : sender;
        const senderKey = event.senderId ? event.senderId.trim().toLowerCase() : '';
        const currentEmailKey = currentUserEmailRef.current ? currentUserEmailRef.current.trim().toLowerCase() : '';
        const isMe = senderKey === currentEmailKey || (!senderKey && !currentEmailKey);

        addMessage({
          role: isMe ? 'user' : 'assistant',
          text: `**${displayLabel}**:\n${rawText}`,
          images: Array.isArray(event.images) && event.images.length ? event.images : undefined,
        });
        activeAssistantIdRef.current = null;
        toolMessageByUseIdRef.current = {};
        toolNameByUseIdRef.current = {};
        latestToolUseIdRef.current = null;
        if (replayingRef.current) replayLastWasDoneRef.current = false;
        return;
      }
      case '_system':
        addMeta(event.text || '');
        if (event.text) addActivity({ phase: 'system', label: 'System event', detail: event.text });
        return;
      case '_done':
        flushAllPending();
        setBusy(false);
        setThinking(null);
        runningToolNameRef.current = null;
        addActivity({
          phase: 'turn',
          label: event.exitCode && event.exitCode !== 0 ? `Turn exited ${event.exitCode}` : 'Turn completed',
          level: event.exitCode && event.exitCode !== 0 ? 'error' : 'done',
        });
        // Wait for the server to record the chat as no longer running before
        // refreshing the list — otherwise the refresh races the status write
        // and the chat dot stays amber instead of turning green.
        if (replayingRef.current) {
          replayLastWasDoneRef.current = true;
        } else if (activeRunIdRef.current) {
          setAltYChatRunning(activeRunIdRef.current, false).then(refreshChats);
        } else {
          refreshChats();
        }
        // Dispatch the oldest message queued against THIS chat; the rest stay
        // queued and go out as each subsequent turn finishes (FIFO). Skipped
        // during replay — a reattach to an already-finished chat flushes the
        // queue from connectFlow once the socket is actually open (dispatching
        // here would hit a not-yet-open socket and silently drop the message).
        if (!replayingRef.current) {
          const doneRunId = activeRunIdRef.current;
          const doneQueue = doneRunId != null ? queuedByRunIdRef.current.get(doneRunId) : undefined;
          if (doneRunId != null && doneQueue && doneQueue.length) {
            const [queued, ...rest] = doneQueue;
            if (rest.length) queuedByRunIdRef.current.set(doneRunId, rest);
            else queuedByRunIdRef.current.delete(doneRunId);
            setQueuedMessages([...rest]);
            window.setTimeout(() => sendMessageRef.current?.(queued.text, queued.effort, queued.images, queued.extra, true), 0);
          }
        }
        return;
      case '_chat_renamed': {
        // The broker auto-named this chat after the turn finished — patch the
        // sidebar row in place (5-word title + summaries) so it updates live
        // without waiting for the next list refresh. Persisted to the
        // transcript so detached dev workers can deliver it via the tailer;
        // the post-replay chats refresh handles consistency on reconnect, so
        // a transcript walk should not re-apply old renames here.
        if (replayingRef.current) return;
        const runId = typeof event.runId === 'number' ? event.runId : Number(event.runId);
        if (!Number.isFinite(runId)) return;
        const title = typeof event.title === 'string' ? event.title : '';
        const overallSummary = typeof event.overallSummary === 'string'
          ? event.overallSummary
          : typeof event.subtitle === 'string'
            ? event.subtitle
            : '';
        const lastTurnSummary = typeof event.lastTurnSummary === 'string' ? event.lastTurnSummary : '';
        setChats((list) => list.map((c) =>
          c.runId === runId ? { ...c, title: title || c.title, subtitle: overallSummary, overallSummary, lastTurnSummary } : c));
        return;
      }
      case '_prefix_estimate': {
        // The broker pushed the next-turn cost estimate after a turn
        // finished — refresh the composer chip in place.
        const payload = (event as { payload?: AltYPrefixEstimate }).payload;
        if (payload && typeof payload === 'object' && typeof payload.tokens === 'number') {
          setPrefixEstimate(payload);
        }
        return;
      }
      case 'system':
        if (event.model) addMeta(`model: ${event.model}`);
        return;
      case 'rate_limit_event':
        if (event.rate_limit_info?.surpassedThreshold) addMeta(`rate limit ${event.rate_limit_info.utilization || ''}`);
        return;
      case 'stream_event': {
        const inner = event.event || {};
        if (inner.type === 'message_start') {
          // A new LLM message inside the same turn — commit whatever the
          // previous message tallied so totals stay monotonic.
          tokensRef.current.committed += tokensRef.current.inFlight;
          tokensRef.current.inFlight = 0;
          if (!replayingRef.current) {
            setThinking((t) => (t ? { ...t, outputTokens: tokensRef.current.committed } : t));
          }
          // End the prior bubble so the next text/thinking event opens a new
          // one. Without this, multi-message turns (text → tool → text → tool →
          // text) pile all the text chunks into a single combined bubble.
          activeAssistantIdRef.current = null;
          return;
        }
        if (inner.type === 'message_delta' && inner.usage && typeof inner.usage.output_tokens === 'number') {
          tokensRef.current.inFlight = inner.usage.output_tokens;
          if (!replayingRef.current) {
            const total = tokensRef.current.committed + tokensRef.current.inFlight;
            setThinking((t) => (t ? { ...t, outputTokens: total } : t));
          }
          return;
        }
        if (inner.type === 'content_block_start') {
          if (inner.content_block?.type === 'tool_use') {
            const toolName = inner.content_block.name || 'tool';
            const toolUseId = inner.content_block.id || `tool-${Date.now()}`;
            addToolMessage(toolUseId, toolName);
            runningToolNameRef.current = toolName;
            addActivity({
              phase: 'tool',
              level: 'tool',
              label: toolName === 'shell' || toolName === 'Bash' ? 'Shell command started' : `Tool started: ${toolName}`,
            });
            if (!replayingRef.current) {
              setThinking((t) => (t ? { ...t, activeTool: toolName } : t));
            }
          }
          return;
        }
        if (inner.type === 'content_block_delta') {
          const delta = inner.delta || {};
          if (delta.type === 'text_delta') appendAssistantText(delta.text || '');
          if (delta.type === 'thinking_delta') appendAssistantText(delta.thinking || '');
          if (delta.type === 'input_json_delta') {
            const partial = delta.partial_json || '';
            const toolUseId = latestToolUseIdRef.current;
            const messageId = toolUseId ? toolMessageByUseIdRef.current[toolUseId] : null;
            if (messageId) {
              if (replayMessagesRef.current) {
                const idx = replayMessagesRef.current.findIndex((item) => item.id === messageId);
                if (idx !== -1) {
                  const item = replayMessagesRef.current[idx];
                  const toolInput = `${item.toolInput || ''}${partial}`;
                  replayMessagesRef.current[idx] = {
                    ...item,
                    toolInput,
                    toolSummary: summarizeToolInput(toolInput) || item.toolSummary,
                  };
                }
              } else {
                setMessages((items) => items.map((item) => {
                  if (item.id !== messageId) return item;
                  const toolInput = `${item.toolInput || ''}${partial}`;
                  return {
                    ...item,
                    toolInput,
                    toolSummary: summarizeToolInput(toolInput) || item.toolSummary,
                  };
                }));
              }
            }
          }
          return;
        }
        return;
      }
      case 'user': {
        const contents = Array.isArray(event.message?.content) ? event.message.content : [event.message?.content].filter(Boolean);
        for (const content of contents) {
          if (content?.type === 'tool_result') {
            const output = formatToolValue(content.content);
            const toolUseId = content.tool_use_id || null;
            const toolName = toolUseId ? toolNameByUseIdRef.current[toolUseId] : runningToolNameRef.current;
            updateToolMessage(content.tool_use_id || null, {
              toolOutput: output,
              toolStatus: content.is_error ? 'error' : 'done',
              toolOutputTruncated: !!content._altyTruncated,
            });
            addActivity({
              phase: 'tool',
              level: content.is_error ? 'error' : 'done',
              label: content.is_error
                ? `Tool failed${toolName ? `: ${toolName}` : ''}`
                : `Tool finished${toolName ? `: ${toolName}` : ''}`,
              detail: output,
            });
            runningToolNameRef.current = null;
            if (!replayingRef.current) {
              setThinking((t) => (t ? { ...t, activeTool: null } : t));
            }
          }
        }
        return;
      }
      case 'assistant': {
        // Fallback when no preceding `stream_event` carried the same content:
        // sub-agent (Agent/Task) inner tool calls arrive only as `assistant`
        // events with tool_use blocks, and the agy/Gemini turn arrives as a
        // single `assistant` event with text. With this branch they render as
        // tool cards / assistant bubbles instead of vanishing. For top-level
        // claude turns this branch is a no-op because each tool_use_id has
        // already been registered via stream_event and the active assistant
        // message already exists.
        const blocks = Array.isArray(event.message?.content) ? event.message.content : [];
        for (const block of blocks) {
          if (!block) continue;
          if (block.type === 'tool_use' && block.id && !toolMessageByUseIdRef.current[block.id]) {
            const toolName = block.name || 'tool';
            addToolMessage(block.id, toolName);
            const input = block.input && typeof block.input === 'object'
              ? JSON.stringify(block.input, null, 2)
              : typeof block.input === 'string' ? block.input : '';
            updateToolMessage(block.id, {
              toolInput: input,
              toolSummary: summarizeToolInput(input),
            });
            addActivity({
              phase: 'tool',
              level: 'tool',
              label: `Tool started: ${toolName}`,
              detail: input,
            });
          } else if (block.type === 'text' && typeof block.text === 'string' && block.text) {
            // Only synthesise an assistant bubble for top-level events that
            // didn't already stream — sub-agent text (parent_tool_use_id set)
            // is already represented by the wrapping Agent tool_result.
            if (!event.parent_tool_use_id && !activeAssistantIdRef.current) {
              const messageId = addMessage({ role: 'assistant', text: block.text });
              activeAssistantIdRef.current = messageId;
            }
          }
        }
        return;
      }
      case 'result': {
        const cost = typeof event.total_cost_usd === 'number' ? `$${event.total_cost_usd.toFixed(4)}` : '?';
        const seconds = ((event.duration_ms || 0) / 1000).toFixed(1);
        addMeta(`${cost} · ${seconds}s · ${event.num_turns || 0} turns`);
        return;
      }
      case '_deep_status':
      case '_deep_ready':
      case '_deep_event':
        addMeta(event.text || event.kind || event.type);
        return;
      case '_dev_env': {
        // Online Dev MVP: the chat's dev fork is up (or refreshed) — capture
        // the URL so the panel can iframe it. Also surface as a meta line in
        // the chat so the user sees the link in the transcript even when
        // they've closed the side panel.
        const url = typeof event.url === 'string' ? event.url : '';
        const name = typeof event.name === 'string' ? event.name : '';
        const statusVal: AltYDevEnv['status'] = event.status === 'starting' || event.status === 'failed'
          ? event.status
          : 'ready';
        if (url) {
          setDevEnv((prev) => ({
            url,
            name,
            status: statusVal,
            proof: event.proof && typeof event.proof === 'object' ? event.proof : prev?.proof,
          }));
          addMeta(`dev env ${name ? `\`${name}\` ` : ''}→ ${url}`);
        }
        return;
      }
      case '_dev_proof': {
        setDevEnv((prev) => prev ? { ...prev, proof: event } : prev);
        const confidence = typeof event.confidence === 'number' ? `${Math.round(event.confidence)}%` : 'reported';
        addMeta(`proof ${confidence}`);
        return;
      }
      case '_dev_merge': {
        addMeta(event.ok ? `merged to ${event.target || 'ai'}` : `merge needs review: ${event.message || 'failed'}`);
        return;
      }
      case '_broker_restart_request': {
        // The dev orchestrator noticed the builder modified the broker source
        // this turn — only a restart picks the change up. We no longer pop a
        // blocking confirm(): the ⚙ Settings cog turns amber and shows a
        // "Restart Alt-Y server" button (driven by /api/alty/broker-status), so
        // just leave a breadcrumb pointing the user there.
        addMeta('Alt-Y server code changed — click the amber "Restart Alt-Y server" button in the ⚙ menu to apply it.');
        return;
      }
      default:
        return;
    }
  }, [addActivity, addMessage, addMeta, addToolMessage, appendAssistantText, flushAllPending, refreshChats, updateToolMessage]);

  const loadToolOutput = useCallback(async (messageId: string, useId: string): Promise<void> => {
    const runId = activeRunIdRef.current;
    if (runId == null || !useId) return;
    try {
      const res = await getAltYToolOutput(runId, useId);
      if (activeRunIdRef.current !== runId) return;
      const output = formatToolValue(res.content);
      setMessages((items) => items.map((message) => (
        message.id === messageId
          ? { ...message, toolOutput: output, toolOutputTruncated: false }
          : message
      )));
    } catch {
      /* Keep the preview flag so the next expansion retries the fetch. */
    }
  }, []);

  const connectSocket = useCallback((wsUrl: string): Promise<void> => {
    if (reconnectTimerRef.current) {
      window.clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
    const oldSocket = wsRef.current;
    if (oldSocket) {
      deliberateCloseRef.current = true;
      try {
        oldSocket.close();
      } catch {
        /* ignore */
      }
    }

    setStatus('connecting');
    deliberateCloseRef.current = false;
    const socket = new WebSocket(wsUrl);
    wsRef.current = socket;

    // Resolves when the socket reaches OPEN (or rejects on close-before-open
    // — which the connectFlow caller catches). Returning the promise lets
    // `await chat.startNewChat()` truly wait for a sendable socket; without
    // it, an immediate sendMessage races the open event and silently drops.
    const ready = new Promise<void>((resolve, reject) => {
      const onOpen = () => {
        socket.removeEventListener('open', onOpen);
        socket.removeEventListener('close', onCloseBeforeOpen);
        resolve();
      };
      const onCloseBeforeOpen = () => {
        socket.removeEventListener('open', onOpen);
        socket.removeEventListener('close', onCloseBeforeOpen);
        reject(new Error('socket closed before open'));
      };
      socket.addEventListener('open', onOpen);
      socket.addEventListener('close', onCloseBeforeOpen, { once: true });
    });

    socket.addEventListener('open', () => {
      if (wsRef.current !== socket) return;
      reconnectAttemptRef.current = 0;
      setConnected(true);
      setStatus('live');
      // Stopping a chat we weren't attached to means attaching first; once its
      // socket is up, deliver the cancel that was requested from the sidebar.
      if (cancelOnAttachRef.current) {
        cancelOnAttachRef.current = false;
        try {
          socket.send(JSON.stringify({ op: 'cancel' }));
        } catch {
          /* ignore */
        }
      }
    });

    socket.addEventListener('close', () => {
      if (wsRef.current !== socket) return;
      setConnected(false);
      if (deliberateCloseRef.current) {
        deliberateCloseRef.current = false;
        setBusy(false);
        setStatus('offline');
        return;
      }
      if (!activeRunIdRef.current) {
        setBusy(false);
        setStatus('offline');
        return;
      }
      // Unexpected drop on an active chat (network blip / broker restart).
      // Leave `busy` untouched so the composer keeps showing the Stop button
      // while we reconnect — forcing it false here made the Stop button vanish
      // for the whole reconnect window even though the turn was still running
      // on the broker. The reattach replay reconciles it (a finished turn
      // replays its `_done` → busy false; a still-running turn replays its
      // unmatched `_user` → busy true).
      const delay = Math.min(reconnectBaseMs * Math.pow(2, reconnectAttemptRef.current), reconnectMaxMs);
      reconnectAttemptRef.current += 1;
      setStatus(`reconnecting ${Math.round(delay / 1000)}s`);
      reconnectTimerRef.current = window.setTimeout(() => {
        reconnectTimerRef.current = null;
        void attachChatRef.current?.(activeRunIdRef.current as number);
      }, delay);
    });

    socket.addEventListener('error', () => {
      if (wsRef.current === socket) setStatus('error');
    });

    socket.addEventListener('message', (message) => {
      // Any inbound frame proves the socket is alive — settle a pending
      // liveness probe (see the resume effect) so we don't tear down a healthy
      // connection.
      if (livenessTimerRef.current) {
        window.clearTimeout(livenessTimerRef.current);
        livenessTimerRef.current = null;
      }
      try {
        const event = JSON.parse(message.data);
        if (event?.type !== '_pong') {
          recordLiveTranscriptEvent(event);
          handleEvent(event);
        }
      } catch {
        addMeta('received malformed message from agent');
      }
    });
    return ready;
  }, [addMeta, handleEvent, recordLiveTranscriptEvent]);

  const connectFlow = useCallback(async (attachToRunId?: number | null, parentRunId?: number | null): Promise<number | null> => {
    const seq = (connectSeqRef.current += 1);
    const isStale = () => connectSeqRef.current !== seq;

    // Snapshot the chat we're leaving into the per-runId cache BEFORE
    // closeSocket/setMessages tear it down. Switching back to it later
    // hydrates instantly from this snapshot instead of flashing empty.
    const leavingRunId = activeRunIdRef.current;
    const leavingMessages = messagesRef.current;
    if (leavingRunId != null && leavingRunId !== attachToRunId) {
      if (chatHasContent(messagesRef.current)) {
        cacheMessages(leavingRunId, messagesRef.current);
        // NB: we do NOT drop the leaving chat's queued messages — they're kept
        // per-runId in queuedByRunIdRef and re-shown when you switch back, so
        // switching chats no longer loses what you queued.
      } else {
        // Started this chat but never used it — reap it as we move on rather
        // than keeping an empty row in the sidebar history.
        reapEmptyChat(leavingRunId);
      }
    }

    closeSocket();
    setBusy(false);
    setStatus('minting');
    setDevEnv(null);
    setActivities([]);
    replayActivitiesRef.current = null;
    setPrefixEstimate(null);
    activeAssistantIdRef.current = null;
    toolMessageByUseIdRef.current = {};
    toolNameByUseIdRef.current = {};
    latestToolUseIdRef.current = null;

    if (attachToRunId) {
      activeRunIdRef.current = attachToRunId;
      setActiveRunId(attachToRunId);
      const cachedEntry = transcriptCacheRef.current.get(attachToRunId);
      const cached = cachedEntry?.messages.length ? cachedEntry.messages : messageCacheRef.current.get(attachToRunId);
      setMessages(cached || []);
      setQueuedMessages([...(queuedByRunIdRef.current.get(attachToRunId) || [])]);
    } else {
      // Fresh chat (or mode switch) — nothing queued yet for it.
      setQueuedMessages([]);
    }

    try {
      const cachedEntry = attachToRunId ? transcriptCacheRef.current.get(attachToRunId) : undefined;
      const afterLine = cachedEntry?.events.length ? cachedEntry.lineCount : undefined;
      const rememberedPageContext = attachToRunId != null
        ? pageContextByRunIdRef.current.get(attachToRunId) ?? null
        : null;
      const contextForConnect = rememberedPageContext || contextRef.current;
      const targetBaseUrl = targetBaseUrlFor(contextForConnect, rememberedPageContext ? null : activeTabRef.current);
      const [minted, transcript] = await Promise.all([
        mintAltYSession(contextForConnect, attachToRunId, modeRef.current, parentRunId, targetBaseUrl),
        attachToRunId ? getAltYTranscript(attachToRunId, afterLine, undefined, true) : Promise.resolve({ events: [] }),
      ]);
      if (isStale()) return null;
      // Participants is purely a header chip; don't block chat-open on it.
      // Start empty (matches a fresh chat) and backfill in the background;
      // the count flips from 1 to the real number as soon as the call returns.
      setParticipants([]);
      const participantsRunId = attachToRunId ?? minted.runId;
      void listAltYParticipants(participantsRunId).then((list) => {
        if (isStale()) return;
        if (activeRunIdRef.current !== participantsRunId) return;
        setParticipants(list || []);
      }).catch(() => undefined);

      const runPageContext = attachToRunId != null
        ? rememberedPageContext || rememberPageContextForRun(
          pageContextByRunIdRef.current,
          minted.runId,
          contextForConnect,
          activeTabRef.current,
        )
        : rememberPageContextForRun(
          pageContextByRunIdRef.current,
          minted.runId,
          contextForConnect,
          activeTabRef.current,
        );

      const socketPromise = connectSocket(minted.wsUrl);

      pageCommandSessionIdRef.current = minted.oneSheetSessionId;
      pageCommandBaseUrlRef.current = minted.oneSheetBaseUrl || targetBaseUrlFor(runPageContext || contextForConnect, null) || targetBaseUrl;
      activeRunIdRef.current = minted.runId;
      if (!transcriptCacheRef.current.has(minted.runId)) {
        transcriptCacheRef.current.set(minted.runId, {
          events: [],
          lineCount: 0,
          messages: [],
          updatedAt: Date.now(),
        });
        schedulePersistedChatCache();
      }
      if (!attachToRunId) {
        const now = new Date().toISOString();
        const isSubchat = typeof minted.parentRunId === 'number';
        const optimisticChat: AltYChatSummary = {
          runId: minted.runId,
          title: minted.title || '',
          subtitle: '',
          overallSummary: '',
          lastTurnSummary: '',
          lastActivity: now,
          started: now,
          status: CHAT_STATUS_COMPLETED,
          mode: modeRef.current,
          parentRunId: minted.parentRunId ?? null,
          rootRunId: minted.rootRunId ?? minted.runId,
          depth: isSubchat ? 1 : 0,
          isSubchat,
        };
        setChats((list) => {
          const index = list.findIndex((chat) => chat.runId === minted.runId);
          if (index === -1) return [optimisticChat, ...list];
          return list.map((chat) => (
            chat.runId === minted.runId
              ? {
                ...chat,
                ...optimisticChat,
                title: optimisticChat.title || chat.title,
                subtitle: chat.subtitle || optimisticChat.subtitle,
                overallSummary: chat.overallSummary || chat.subtitle || optimisticChat.overallSummary,
                lastTurnSummary: chat.lastTurnSummary || optimisticChat.lastTurnSummary,
                started: chat.started || optimisticChat.started,
              }
              : chat
          ));
        });
      }
      setActiveRunId(minted.runId);
      setQueuedMessages([...(queuedByRunIdRef.current.get(minted.runId) || [])]);
      try {
        localStorage.setItem(lastRunKeyFor(modeRef.current), String(minted.runId));
      } catch {
        /* ignore */
      }

      // Switching chats no longer drags the iframe along — we used to call
      // crossServerNavigate(runPageContext.url) here to restore "where this
      // chat was last bound". That made every chat-switch reload the right
      // pane unexpectedly. The chat's remembered page is still kept (for the
      // mint's customer/base binding), but the iframe stays where the user
      // left it; the per-turn pageContext block below carries the live page.

      // Whether to flush this chat's queued messages once the socket is open.
      // Default true (a fresh/finished chat sends them now); flipped false below
      // if the chat turns out to still be running, in which case the queue waits
      // for the next live `_done` instead.
      let flushQueueAfterConnect = true;

      const sameChatReconnect = minted.reattached && activeRunIdRef.current === minted.runId && !userSentRef.current && allowAutoRenameRef.current;
      allowAutoRenameRef.current = !minted.reattached || sameChatReconnect;
      userSentRef.current = minted.reattached ? userSentRef.current : false;

      if (attachToRunId && minted.reattached) {
        const mergedTranscript = mergeTranscriptEvents(cachedEntry, transcript);
        try {
          replayingRef.current = true;
          replayLastWasDoneRef.current = false;
          replayMessagesRef.current = [];
          replayActivitiesRef.current = [];
          activeAssistantIdRef.current = null;
          toolMessageByUseIdRef.current = {};
          toolNameByUseIdRef.current = {};
          latestToolUseIdRef.current = null;
          for (const event of mergedTranscript.events) handleEvent(event);
          const replayedMessages = replayMessagesRef.current;
          const replayedActivities = replayActivitiesRef.current || [];
          setMessages(replayedMessages);
          setActivities(replayedActivities.slice(-ACTIVITY_LIMIT));
          cacheTranscript(minted.runId, mergedTranscript.events, mergedTranscript.lineCount, replayedMessages);
        } finally {
          replayingRef.current = false;
          replayMessagesRef.current = null;
          replayActivitiesRef.current = null;
        }
        // Replay suppressed both the live status writes and the thinking
        // indicator. Reconcile against the server now:
        //  - If the transcript ended with `_done` but the server still has the
        //    chat flagged as running (we missed the live `_done` while
        //    detached), flip the flag so the sidebar dot turns green. Only do
        //    this when actually flagged running — `setAltYChatRunning` bumps
        //    `last_activity` and would otherwise reorder the sidebar on every
        //    click into an already-completed chat.
        //  - If the transcript ended mid-turn, the chat is still running but
        //    we have no visible indicator (because the bebop indicator was
        //    suppressed during replay). Restore it, anchored to the server's
        //    `last_activity` for this chat — that's when the current turn was
        //    marked running, so the elapsed counter shows the real time since
        //    the turn started, not since we reattached.
        if (activeRunIdRef.current === minted.runId) {
          let currentChat: AltYChatSummary | undefined;
          try {
            const list = await listAltYChats();
            if (isStale()) return null;
            setChats(list);
            lastChatsRefreshAtRef.current = Date.now();
            currentChat = list.find((c) => c.runId === minted.runId);
          } catch {
            currentChat = chatsRef.current.find((c) => c.runId === minted.runId);
          }
          const stillRunningOnServer = currentChat?.status === CHAT_STATUS_RUNNING;
          if (replayLastWasDoneRef.current) {
            replayLastWasDoneRef.current = false;
            if (stillRunningOnServer) void setAltYChatRunning(minted.runId, false);
          } else if (stillRunningOnServer && currentChat) {
            const parsed = Date.parse(currentChat.lastActivity);
            const startedAt = Number.isFinite(parsed) ? parsed : Date.now();
            setThinking({
              startedAt,
              outputTokens: tokensRef.current.committed + tokensRef.current.inFlight,
              activeTool: runningToolNameRef.current,
            });
            // Turn is still running — let its queue wait for the live `_done`.
            flushQueueAfterConnect = false;
          }
        }
      } else {
        toolMessageByUseIdRef.current = {};
        toolNameByUseIdRef.current = {};
        latestToolUseIdRef.current = null;
        setMessages([]);
        setActivities([]);
      }

      // Wait for the WS to be OPEN before resolving — without this, callers
      // that immediately chain a sendMessage hit a CONNECTING socket and
      // their message silently drops. Failure here is non-fatal: the close
      // handler already kicks off a reconnect.
      try {
        await socketPromise;
      } catch {
        /* close-before-open is handled by the close listener's reconnect */
      }
      if (isStale()) return null;
      // The chat finished (or was never running) while we were away but still
      // has messages queued against it — now that the socket is open, send the
      // oldest. Its `_done` then chains the rest (FIFO) via the handler above.
      if (flushQueueAfterConnect && activeRunIdRef.current === minted.runId) {
        const q = queuedByRunIdRef.current.get(minted.runId);
        if (q && q.length) {
          const [queued, ...rest] = q;
          if (rest.length) queuedByRunIdRef.current.set(minted.runId, rest);
          else queuedByRunIdRef.current.delete(minted.runId);
          setQueuedMessages([...rest]);
          window.setTimeout(() => sendMessageRef.current?.(queued.text, queued.effort, queued.images, queued.extra, true), 0);
        }
      }
      connectFailureNoticeRunIdRef.current = null;
      // Skip when the sidebar was just pulled — back-to-back chat switches
      // would otherwise fire an HTTP roundtrip + reset the chats array
      // reference (invalidating the panel's arrangeChatTree/Map memos) on
      // every click. listAltYChats also runs on panel open, after rename /
      // delete / mode-flip, and on the live `_done` handler, so a 2-second
      // throttle here doesn't make the sidebar feel stale.
      if (Date.now() - lastChatsRefreshAtRef.current > 2000) refreshChats();
      return minted.runId;
    } catch (error) {
      if (isStale()) return null;
      cancelOnAttachRef.current = false;
      if (attachToRunId) {
        activeRunIdRef.current = leavingRunId ?? null;
        setActiveRunId(leavingRunId ?? null);
        if (leavingRunId != null) {
          const cachedEntry = transcriptCacheRef.current.get(leavingRunId);
          const cached = cachedEntry?.messages.length ? cachedEntry.messages : messageCacheRef.current.get(leavingRunId);
          setMessages(cached || leavingMessages);
          setQueuedMessages([...(queuedByRunIdRef.current.get(leavingRunId) || [])]);
        } else {
          setMessages([]);
          setActivities([]);
          setQueuedMessages([]);
        }
      }
      if (attachToRunId != null && isTransientConnectError(error)) {
        const delay = Math.min(reconnectBaseMs * Math.pow(2, reconnectAttemptRef.current), reconnectMaxMs);
        reconnectAttemptRef.current += 1;
        setStatus(`reconnecting ${Math.round(delay / 1000)}s`);
        if (connectFailureNoticeRunIdRef.current !== attachToRunId) {
          connectFailureNoticeRunIdRef.current = attachToRunId;
          addMeta('Alt-Y server is restarting; reconnecting automatically.');
        }
        if (reconnectTimerRef.current) window.clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = window.setTimeout(() => {
          reconnectTimerRef.current = null;
          if (activeRunIdRef.current === attachToRunId) {
            void attachChatRef.current?.(attachToRunId);
          }
        }, delay);
        return null;
      }
      setStatus('error');
      addMeta(`connect failed: ${connectErrorMessage(error)}`);
      return null;
    }
  }, [addMeta, cacheMessages, cacheTranscript, closeSocket, connectSocket, handleEvent, refreshChats, reapEmptyChat, schedulePersistedChatCache]);

  // Attach to an existing chat from the unified sidebar list. The chat's mode
  // may differ from the panel's current mode (e.g. clicking an agent-factory
  // chat while interact is active); we look up the chat row and flip the panel
  // mode BEFORE minting, because the broker's /api/alty/mint validates the
  // requested mode against the chat row and 404s on a mismatch.
  const attachChat = useCallback(
    async (runId: number) => {
      const chat = chatsRef.current.find((c) => c.runId === runId);
      if (chat && chat.mode && chat.mode !== modeRef.current) {
        modeRef.current = chat.mode;
        setMode(chat.mode);
        try {
          localStorage.setItem(modeKey, chat.mode);
        } catch {
          /* ignore */
        }
      }
      return await connectFlow(runId);
    },
    [connectFlow],
  );
  const startNewChat = useCallback(() => {
    return connectFlow(null, null);
  }, [connectFlow]);

  const startSubchat = useCallback(() => {
    const currentRunId = activeRunIdRef.current;
    return connectFlow(null, currentRunId ?? null);
  }, [connectFlow]);

  const setPendingPrimer = useCallback((primer: string | null) => {
    pendingPrimerRef.current = primer;
  }, []);

  const setPendingAuto = useCallback((auto: boolean) => {
    pendingAutoRef.current = auto;
  }, []);

  const sendMessage = useCallback((text: string, effort = 'high', images: AltYImageAttachment[] = [], extra: SendExtra = {}, fromQueue = false) => {
    const trimmed = text.trim();
    if (!trimmed && images.length === 0) return;
    const socket = wsRef.current;
    if (!socket || socket.readyState !== WebSocket.OPEN) {
      addMeta('not connected');
      return;
    }
    // `fromQueue` is the dispatch of a previously-queued message after its
    // turn's `_done`. We must NOT re-queue it just because the `busy` closure
    // here hasn't observed the `setBusy(false)` yet (the re-render that
    // refreshes this closure can lose a race with the dispatch's setTimeout) —
    // otherwise the message stays queued forever and never submits.
    if (busy && !fromQueue) {
      const runId = activeRunIdRef.current;
      if (runId != null) {
        const next = [
          ...(queuedByRunIdRef.current.get(runId) || []),
          { id: id('queued'), text: trimmed, images, effort, extra },
        ];
        queuedByRunIdRef.current.set(runId, next);
        setQueuedMessages([...next]);
      }
      return;
    }
    const primer = pendingPrimerRef.current;
    pendingPrimerRef.current = null;
    const auto = pendingAutoRef.current;
    pendingAutoRef.current = false;
    const finalText = primer
      ? `${primer}${AGENT_PRIMER_SEPARATOR}${trimmed}`
      : trimmed;
    // `mode` rides along on every turn so the broker can flip the chat in
    // place when the user switched to a workspace-compatible agent mid-chat
    // (no-op when it already matches the live session's mode). `auto` tells the
    // broker to wrap this turn with AUTO_PROMPT (self-route over all agents).
    const consensusModels = extra.models && extra.models.length >= 2 ? extra.models : null;
    const payload: Record<string, unknown> = consensusModels
      ? { op: 'send_consensus', text: finalText, models: consensusModels, mode: modeRef.current }
      : { op: 'send', text: finalText, effort, mode: modeRef.current };
    if (!consensusModels) {
      if (extra.codexEffort) payload.codexEffort = extra.codexEffort;
      if (extra.fast) payload.fast = true;
      if (extra.max) payload.max = true;
      if (auto) payload.auto = true;
    }
    if (images.length) payload.images = images;
    const runId = activeRunIdRef.current;
    // Each turn's pageContext is the LIVE iframe state — the user may have
    // navigated since the chat was minted (or since the previous turn), and the
    // agent's next reply should refer to where they are NOW. If the live
    // context isn't available yet (panel just opened, bridge handshake hasn't
    // landed), fall back to the chat's remembered snapshot. Refresh the
    // remembered snapshot in passing so a later reconnect still has a sensible
    // mint context to start from.
    const liveCtx = contextRef.current;
    const liveTab = activeTabRef.current;
    if (runId != null && liveCtx) {
      rememberPageContextForRun(pageContextByRunIdRef.current, runId, liveCtx, liveTab);
    }
    const sendCtx = liveCtx
      || (runId != null ? pageContextByRunIdRef.current.get(runId) ?? null : null);
    const targetBaseUrl = targetBaseUrlFor(sendCtx, liveTab);
    pageCommandBaseUrlRef.current = targetBaseUrl || pageCommandBaseUrlRef.current;
    payload.pageContext = {
      url: sendCtx?.url ?? null,
      origin: sendCtx?.origin ?? originFromUrl(sendCtx?.url),
      baseUrl: targetBaseUrl,
      documentId: sendCtx?.documentId ?? null,
      documentTypeId: sendCtx?.documentTypeId ?? null,
      visibleFields: sendCtx?.visibleFields ?? null,
      selectedRows: sendCtx?.selectedRows ?? null,
      selectedDocumentRows: sendCtx?.selectedDocumentRows ?? null,
      customerHost: sendCtx?.customerHost ?? null,
      customerId: sendCtx?.customerId ?? null,
      customerName: sendCtx?.customerName ?? null,
      tab: tabPayloadForContext(sendCtx, liveTab),
    };
    socket.send(JSON.stringify(payload));
    if (allowAutoRenameRef.current && activeRunIdRef.current) {
      allowAutoRenameRef.current = false;
      const title = trimmed.replace(/\s+/g, ' ').slice(0, 30);
      // auto=true: a placeholder so the row isn't blank while the turn runs.
      // The broker replaces it with a 5-word summary once the turn finishes;
      // passing auto keeps the chat unlocked so that override is allowed.
      if (title) renameAltYChat(activeRunIdRef.current, title, true).then(refreshChats).catch(() => undefined);
    }
    userSentRef.current = true;
  }, [addMeta, busy, refreshChats]);

  useEffect(() => {
    sendMessageRef.current = sendMessage;
  }, [sendMessage]);

  useEffect(() => {
    attachChatRef.current = attachChat;
  }, [attachChat]);

  // Re-establish the socket NOW, skipping the exponential backoff. The close
  // handler's backoff can grow to 30s and, worse, its setTimeout is frozen
  // while a mobile tab is backgrounded — so without this a user returning to a
  // suspended tab can sit on "Connection lost" far too long (or forever, if a
  // reconnect mint failed and wedged into the error state). Resetting the
  // attempt counter + clearing any scheduled retry makes the next attempt fire
  // immediately. No-op when there's no chat to reconnect to.
  const forceReconnect = useCallback(() => {
    const runId = activeRunIdRef.current;
    if (runId == null) return;
    if (reconnectTimerRef.current) {
      window.clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
    reconnectAttemptRef.current = 0;
    void attachChatRef.current?.(runId);
  }, []);

  // When the user comes back to the app — refocuses the window, unhides a
  // backgrounded tab, or the network returns — recover the connection without a
  // page reload. A backgrounded mobile tab routinely has its WebSocket killed
  // by the OS; sometimes the close fires (→ "Connection lost", socket CLOSED)
  // and sometimes the socket is left as a dead-but-OPEN zombie. Handle both:
  // reconnect outright when the socket isn't OPEN, and probe with a ping when it
  // claims to be OPEN, reconnecting only if no frame answers in time.
  useEffect(() => {
    if (!isOpen) return;
    const probeLiveness = (socket: WebSocket) => {
      if (livenessTimerRef.current != null) return; // a probe is already in flight
      try {
        socket.send(JSON.stringify({ op: 'ping' }));
      } catch {
        forceReconnect();
        return;
      }
      livenessTimerRef.current = window.setTimeout(() => {
        livenessTimerRef.current = null;
        // No pong (nor any other frame) came back — the socket is dead.
        if (wsRef.current === socket && socket.readyState === WebSocket.OPEN) forceReconnect();
      }, 6000);
    };
    const onResume = () => {
      if (document.visibilityState !== 'visible') return;
      if (activeRunIdRef.current == null) return;
      const socket = wsRef.current;
      const state = socket ? socket.readyState : WebSocket.CLOSED;
      if (state === WebSocket.CONNECTING) return; // a connect is already underway
      if (state === WebSocket.OPEN && socket) {
        probeLiveness(socket);
        return;
      }
      forceReconnect();
    };
    document.addEventListener('visibilitychange', onResume);
    window.addEventListener('focus', onResume);
    window.addEventListener('online', onResume);
    window.addEventListener('pageshow', onResume);
    return () => {
      document.removeEventListener('visibilitychange', onResume);
      window.removeEventListener('focus', onResume);
      window.removeEventListener('online', onResume);
      window.removeEventListener('pageshow', onResume);
    };
  }, [isOpen, forceReconnect]);

  const stop = useCallback(() => {
    const socket = wsRef.current;
    if (socket?.readyState === WebSocket.OPEN) socket.send(JSON.stringify({ op: 'cancel' }));
  }, []);

  // Drop one message queued behind the active chat's running turn before it
  // gets dispatched.
  const cancelQueued = useCallback((queuedId: string) => {
    const runId = activeRunIdRef.current;
    if (runId == null) return;
    const list = queuedByRunIdRef.current.get(runId);
    if (!list) return;
    const next = list.filter((q) => q.id !== queuedId);
    if (next.length) queuedByRunIdRef.current.set(runId, next);
    else queuedByRunIdRef.current.delete(runId);
    setQueuedMessages([...next]);
  }, []);

  const refreshGroups = useCallback(async () => {
    try {
      setChatGroups(await listAltYChatGroups());
    } catch {
      // The endpoint may not yet exist on an older broker — degrade silently
      // so the rest of the sidebar still works.
    }
  }, []);

  const createGroup = useCallback(async (label: string) => {
    const created = await createAltYChatGroup(label);
    if (created) {
      setChatGroups((list) => [created, ...list.filter((g) => g.id !== created.id)]);
    }
    return created;
  }, []);

  const renameGroup = useCallback(async (groupId: number, label: string) => {
    const trimmed = label.trim();
    if (!trimmed) return null;
    const updated = await updateAltYChatGroup(groupId, { label: trimmed.slice(0, 160) });
    if (updated) {
      setChatGroups((list) => list.map((g) => (g.id === updated.id ? updated : g)));
    }
    return updated;
  }, []);

  const removeGroup = useCallback(async (groupId: number) => {
    const ok = await deleteAltYChatGroup(groupId);
    if (ok) {
      setChatGroups((list) => list.filter((g) => g.id !== groupId));
      // Chats in the folder become ungrouped — patch the in-memory list so the
      // sidebar reflects it immediately, without a server roundtrip.
      setChats((list) => list.map((c) => (c.groupId === groupId ? { ...c, groupId: null } : c)));
    }
    return ok;
  }, []);

  const moveChatToGroup = useCallback(async (runId: number, groupId: number | null) => {
    // Optimistic — the broker also moves subchats so they stay with the family.
    setChats((list) => {
      const target = list.find((c) => c.runId === runId);
      if (!target) return list;
      const rootRunId = target.rootRunId ?? target.runId;
      return list.map((c) =>
        c.runId === rootRunId || c.rootRunId === rootRunId
          ? { ...c, groupId }
          : c,
      );
    });
    const ok = await setAltYChatGroup(runId, groupId);
    if (!ok) {
      // Re-sync from the source of truth if the optimistic update was wrong.
      try {
        setChats(await listAltYChats());
      } catch { /* swallow */ }
    }
    return ok;
  }, []);

  const renameChat = useCallback(async (runId: number, title: string) => {
    const trimmed = title.trim();
    if (!trimmed) return;
    // Manual rename — suppress the first-message auto-rename so we don't lose
    // the user-chosen title on the next send.
    if (runId === activeRunIdRef.current) allowAutoRenameRef.current = false;
    try {
      await renameAltYChat(runId, trimmed.slice(0, 80));
      await refreshChats();
    } catch (error) {
      addMeta(`rename failed: ${(error as Error).message || error}`);
      throw error;
    }
  }, [addMeta, refreshChats]);

  // Stop any chat from the sidebar. The current chat can be cancelled over its
  // live socket; for another chat we attach to it and cancel once connected.
  const stopChat = useCallback((runId: number) => {
    if (runId === activeRunIdRef.current) {
      stop();
      return;
    }
    cancelOnAttachRef.current = true;
    void attachChat(runId);
  }, [stop, attachChat]);

  const removeChat = useCallback(async (runId: number) => {
    await deleteAltYChat(runId);
    messageCacheRef.current.delete(runId);
    transcriptCacheRef.current.delete(runId);
    writePersistedChatCache(transcriptCacheRef.current);
    // The chat is gone — drop its queued messages too.
    queuedByRunIdRef.current.delete(runId);
    if (activeRunIdRef.current !== runId) {
      await refreshChats();
      return;
    }
    // Deleting the chat we're currently in: switch to the next existing chat
    // rather than minting a fresh one — minting always re-grew the list, so
    // deleting the active chat looked like it did nothing.
    activeRunIdRef.current = null;
    setActiveRunId(null);
    closeSocket();
    setMessages([]);
    setActivities([]);
    setQueuedMessages([]);
    let next: number | null = null;
    try {
      const remaining = await listAltYChats();
      setChats(remaining);
      // Prefer the next chat in the same mode so deleting doesn't yank the
      // panel into a different mode by surprise; fall back to any remaining
      // chat otherwise.
      next = remaining.find((chat) => chat.runId !== runId && chat.mode === modeRef.current)?.runId
        ?? remaining.find((chat) => chat.runId !== runId)?.runId
        ?? null;
    } catch {
      /* fall back to a fresh chat */
    }
    if (next) await attachChat(next);
    else await startNewChat();
  }, [attachChat, closeSocket, refreshChats, startNewChat]);

  const copyTranscript = useCallback(async () => {
    const condensed = buildCondensedTranscript(messagesRef.current);
    if (!condensed.trim()) {
      addMeta('nothing to copy yet');
      return;
    }
    try {
      await writeToClipboard(condensed);
      addMeta('copied chat to clipboard');
    } catch (error) {
      addMeta(`copy failed: ${(error as Error).message || error}`);
    }
  }, [addMeta]);

  const improveFromChat = useCallback(() => {
    const socket = wsRef.current;
    if (!socket || socket.readyState !== WebSocket.OPEN) {
      addMeta('not connected');
      return;
    }
    const prompt = 'Reflect on this chat and identify every point of friction or repeated mistake worth preventing. Persist each useful fix into the centralized memory or skills store using the narrowest fitting scope, and confirm what you wrote.';
    socket.send(JSON.stringify({ op: 'send', text: prompt, effort: 'xhigh' }));
  }, [addMeta]);

  // "Improve agent": reflect on THIS chat against the active agent's own
  // process diagram, then rewrite the agent so a future run goes better. Sent
  // as a plain turn (no agent primer — the prompt itself carries the agent's
  // definition); App.tsx adopts the improved FlowDoc the reply emits and saves
  // it back onto the user agent. Returns false if it couldn't dispatch.
  const improveAgent = useCallback(
    (label: string, flow: FlowDoc) => {
      const socket = wsRef.current;
      if (!socket || socket.readyState !== WebSocket.OPEN) {
        addMeta('not connected');
        return false;
      }
      if (busy) {
        addMeta('busy — wait for the current turn to finish');
        return false;
      }
      const prompt = [
        `You have been running in this chat as the "${label}" agent. This is the agent's current definition — its process diagram:`,
        '',
        '```json',
        JSON.stringify(flow, null, 2),
        '```',
        '',
        'Reflect on THIS chat — the work you just did as this agent — against that definition. Diagnose specifically:',
        '- Where you hit friction, backtracked, did redundant work, or misread the page or the request.',
        '- Any mistakes you made and their root cause.',
        "- If the task wasn't actually accomplished, what blocked it.",
        '',
        'Then improve the agent so a future run of the same kind of task goes more efficiently and correctly — or works at all if it did not this time. Tighten or reorder the steps, add the missing instruction / decision / guardrail, and drop whatever was wasteful. Keep it minimal: change only what the diagnosis justifies.',
        '',
        'If the definition includes a "spec" object (the written brief: "original" = the user\'s request, "clarified" = a clear rewrite, "detailed" = a detailed spec with inferred additions wrapped in [+ … +]), treat it as the agent\'s intent — make sure your changes still satisfy it, and re-emit the spec, refreshing "clarified"/"detailed" if your changes affect them. Keep "original" verbatim and keep inferred additions wrapped in [+ … +]. Preserve the spec if you keep it unchanged.',
        '',
        'Reply with a short plain-language summary of what you changed and why, then emit the agent\'s COMPLETE improved definition as ONE fenced ```json``` block in the SAME shape as above (a FlowDoc: a "title" plus a "steps" array of {kind, text}; keep a "description" and the "spec" if present). Emit the whole document, not a diff.',
      ].join('\n');
      socket.send(JSON.stringify({ op: 'send', text: prompt, effort: 'xhigh' }));
      addMeta(`improving “${label}” from this chat`);
      return true;
    },
    [addMeta, busy],
  );

  const deepImprove = useCallback(() => {
    const socket = wsRef.current;
    if (!socket || socket.readyState !== WebSocket.OPEN) {
      addMeta('not connected');
      return;
    }
    socket.send(JSON.stringify({ op: 'deep_improve', guidance: '' }));
    addMeta('deep-improve launched');
  }, [addMeta]);

  // Promote the Alt-Y surface from the online21 `ai` branch to `testing`.
  // The broker stages a clean promotion branch, resolves the known shared-file
  // conflicts with the builder, then fast-forward pushes testing. Progress and
  // conflict notes stream as `_deep_*` events.
  const promoteAiToTesting = useCallback(() => {
    const socket = wsRef.current;
    if (!socket || socket.readyState !== WebSocket.OPEN) {
      addMeta('not connected');
      return;
    }
    socket.send(JSON.stringify({ op: 'promote_ai_testing' }));
    addMeta('promotion launched — promoting Alt-Y from ai to testing');
  }, [addMeta]);

  // Promote online21 `testing` to `master`, then create the live tag.
  const promoteTestingToLive = useCallback(() => {
    const socket = wsRef.current;
    if (!socket || socket.readyState !== WebSocket.OPEN) {
      addMeta('not connected');
      return;
    }
    socket.send(JSON.stringify({ op: 'promote_testing_live' }));
    addMeta('live promotion launched — merging testing to master and creating the live tag');
  }, [addMeta]);

  // Dispatch the "Edit agent" action — sends the new target diagram for a
  // built-in pin so the broker can spin up a dev orchestrator turn that
  // updates the pin's implementation to match. Triggers the broker-restart
  // confirm flow on completion if the dev agent touched main.py.
  const editPinnedAgent = useCallback(
    (pinId: string, flow: FlowDoc, previousFlow: FlowDoc | null) => {
      const socket = wsRef.current;
      if (!socket || socket.readyState !== WebSocket.OPEN) {
        addMeta('not connected');
        return;
      }
      if (busy) {
        addMeta('busy — wait for the current turn to finish');
        return;
      }
      socket.send(JSON.stringify({ op: 'edit_pinned_agent', pinId, flow, previousFlow }));
    },
    [addMeta, busy],
  );

  // "Make agent" — fired when the user clicks the button on the Agent Factory
  // canvas for a brand-new (not-editing-a-builtin) diagram. Sends the accepted
  // FlowDoc + the recent originating user/assistant chat messages over the WS
  // so the broker can dispatch a dev orchestrator turn that writes the
  // per-agent impl doc (and, for complex agents needing net-new tools, makes
  // any additional code changes). The React shell saves the agent into
  // localStorage independently of this call so the pin shows up immediately.
  const makeAgent = useCallback(
    (flow: FlowDoc, originatingMessages: AltYChatMessage[]) => {
      const socket = wsRef.current;
      if (!socket || socket.readyState !== WebSocket.OPEN) {
        addMeta('not connected');
        return false;
      }
      if (busy) {
        addMeta('busy — wait for the current turn to finish');
        return false;
      }
      // Trim to a sane size for the prompt: only role + text, last 12 entries,
      // truncate per-message text to avoid blowing the prompt with tool output.
      const slim = originatingMessages
        .filter((m) => m.role === 'user' || m.role === 'assistant')
        .slice(-12)
        .map((m) => ({
          role: m.role,
          text: (m.text || '').slice(0, 2000),
        }));
      socket.send(
        JSON.stringify({ op: 'make_agent', flow, originatingMessages: slim }),
      );
      return true;
    },
    [addMeta, busy],
  );

  useEffect(() => {
    if (!isOpen) return;
    refreshChats();
    void refreshGroups();
    if (openedOnceRef.current) return;
    openedOnceRef.current = true;
    void (async () => {
      try {
        const list = await listAltYChats();
        setChats(list);
        // Initial chat to open: prefer the last-opened chat for the panel's
        // current mode (the per-mode `lastRunKey` is still kept as a hint), then
        // the most-recent chat in that mode, then the most-recent overall.
        const lastStored = Number.parseInt(localStorage.getItem(lastRunKeyFor(modeRef.current)) || '0', 10);
        const match = lastStored ? list.find((chat) => chat.runId === lastStored) : null;
        const inMode = list.find((chat) => chat.mode === modeRef.current);
        const runId = match?.runId || inMode?.runId || list[0]?.runId || null;
        if (runId) await attachChat(runId);
        else await startNewChat();
      } catch {
        await startNewChat();
      }
    })();
  }, [attachChat, isOpen, refreshChats, startNewChat]);

  // Switch panel mode. The unified chat list is no longer per-mode, so this
  // just flips the panel into a fresh context for the new mode and leaves
  // chat selection up to the caller (attachChat on a row, or startNewChat).
  // The previous auto-attach-to-last-stored-in-mode behaviour is gone — the
  // sidebar list makes that picker explicit now.
  const switchMode = useCallback(async (next: AltYMode) => {
    if (next === modeRef.current) return;
    modeRef.current = next;
    setMode(next);
    try {
      localStorage.setItem(modeKey, next);
    } catch {
      /* ignore */
    }
    // Switching modes abandons the current chat. If it was never used (no
    // messages either way), reap it so it doesn't linger as an empty sidebar
    // row — switchMode nulls activeRunId below, so the subsequent connectFlow
    // can't catch it.
    const leavingRunId = activeRunIdRef.current;
    if (leavingRunId != null && !chatHasContent(messagesRef.current)) {
      reapEmptyChat(leavingRunId);
    }
    activeRunIdRef.current = null;
    setActiveRunId(null);
    closeSocket();
    setMessages([]);
    setActivities([]);
    // Clear only the displayed queue — per-chat queues stay in queuedByRunIdRef
    // and re-show when you open one of those chats again.
    setQueuedMessages([]);
  }, [closeSocket, reapEmptyChat]);

  // Switch the CURRENT chat's mode IN PLACE — same runId, same socket, same
  // message list. This is how the panel lets the user pick a different agent
  // mid-conversation without forking a fresh chat: interact / agent-factory /
  // bmad share the broker workspace + claude --resume session, so only the
  // per-turn prompt wrapper changes. The new mode rides out on the next send
  // (sendMessage carries `mode`), where the broker flips the live session and
  // persists the chat row. Unlike switchMode, this keeps the conversation up,
  // so it must NOT be used to switch into a dev-orchestrator mode.
  const switchChatMode = useCallback((next: AltYMode) => {
    if (next === modeRef.current) return;
    modeRef.current = next;
    setMode(next);
    try {
      localStorage.setItem(modeKey, next);
    } catch {
      /* ignore */
    }
    const runId = activeRunIdRef.current;
    if (runId != null) {
      setChats((list) => list.map((c) => (c.runId === runId ? { ...c, mode: next } : c)));
    }
  }, []);

  useEffect(() => () => closeSocket(), [closeSocket]);

  useEffect(() => {
    if (!isOpen) return;
    let stopped = false;
    let inFlight = false;

    async function poll() {
      if (stopped || inFlight) return;
      const sessionId = pageCommandSessionIdRef.current;
      if (!sessionId) return;
      const commandBaseUrl = pageCommandBaseUrlRef.current || targetBaseUrlFor(contextRef.current, activeTabRef.current);
      inFlight = true;
      try {
        const commandFrame = frameForBaseUrlRef.current?.(commandBaseUrl) || appFrameRef.current;
        const pending = await pollPendingPageCommand(sessionId, commandBaseUrl, commandFrame);
        if (pending.pending && pending.commandId && pending.action) {
          let result;
          try {
            const targetUrl = pending.action === 'navigate'
              ? navigationTargetUrl(pending.payload, commandBaseUrl || targetBaseUrlFor(contextRef.current, activeTabRef.current))
              : null;
            const targetOrigin = originFromUrl(targetUrl);
            const commandOrigin = originFromUrl(commandBaseUrl || targetBaseUrlFor(contextRef.current, activeTabRef.current));
            if (targetUrl && targetOrigin && commandOrigin && targetOrigin !== commandOrigin && crossServerNavigateRef.current) {
              const nav = await crossServerNavigateRef.current(targetUrl);
              result = nav.ok
                ? {
                    ok: true,
                    crossServer: true,
                    url: nav.url || targetUrl,
                    baseUrl: nav.baseUrl || targetOrigin,
                    tab: nav.tab || null,
                    message: `Switched Alt-Y to ${nav.tab?.label || targetOrigin}.`,
                  }
                : {
                    ok: false,
                    crossServer: true,
                    url: targetUrl,
                    baseUrl: nav.baseUrl || targetOrigin,
                    tab: nav.tab || null,
                    error: nav.error || `Could not switch Alt-Y to ${targetOrigin}.`,
                  };
            } else {
              result = await executePageCommand(commandFrame, pending.action, pending.payload);
            }
          } catch (error) {
            result = { ok: false, error: (error as Error).message || String(error) };
          }
          await postPageCommandResult(pending.commandId, result, commandBaseUrl, commandFrame);
        }
      } catch {
        /* Keep polling; transient auth/navigation failures are expected during page loads. */
      } finally {
        inFlight = false;
      }
    }

    const timer = window.setInterval(poll, 600);
    void poll();
    return () => {
      stopped = true;
      window.clearInterval(timer);
    };
  }, [isOpen]);

  // Background-chat reconciliation. The broker only flips a chat's `running`
  // flag when a client tells it to — so a chat that finishes while we're not
  // attached to its WebSocket stays amber in the sidebar forever. Poll the
  // list and, for any non-active chat still marked running, peek at the
  // transcript: if its latest turn completed (a `_done` after the last
  // `_user`, see transcriptTurnComplete), the chat is actually finished and we
  // write the status back ourselves so the dot turns green without the user
  // having to click into it.
  useEffect(() => {
    if (!isOpen) return;
    let stopped = false;
    let inFlight = false;

    async function tick() {
      if (stopped || inFlight) return;
      inFlight = true;
      try {
        const list = await listAltYChats();
        if (stopped) return;
        setChats(list);
        const candidates = list.filter(
          (chat) =>
            (chat.status === 0 || chat.status === 1) &&
            chat.runId !== activeRunIdRef.current,
        );
        let reconciled = false;
        for (const chat of candidates) {
          try {
            const cachedEntry = transcriptCacheRef.current.get(chat.runId);
            const transcript = await getAltYTranscript(chat.runId, cachedEntry?.events.length ? cachedEntry.lineCount : undefined);
            if (stopped) return;
            const mergedTranscript = mergeTranscriptEvents(cachedEntry, transcript);
            cacheTranscript(
              chat.runId,
              mergedTranscript.events,
              mergedTranscript.lineCount,
              cachedEntry?.messages || [],
            );
            const events = mergedTranscript.events as Array<{ type?: string }>;
            if (transcriptTurnComplete(events)) {
              await setAltYChatRunning(chat.runId, false);
              reconciled = true;
            }
          } catch {
            /* ignore — retry on the next tick */
          }
        }
        if (reconciled && !stopped) {
          try {
            const refreshed = await listAltYChats();
            if (!stopped) setChats(refreshed);
          } catch {
            /* ignore */
          }
        }
      } catch {
        /* ignore — transient errors are fine, we'll retry */
      } finally {
        inFlight = false;
      }
    }

    const timer = window.setInterval(tick, 4000);
    return () => {
      stopped = true;
      window.clearInterval(timer);
    };
  }, [cacheTranscript, isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    let stopped = false;
    let inFlight = false;

    async function tick() {
      if (stopped || inFlight || busy) return;
      const runId = activeRunIdRef.current;
      if (runId == null) return;
      const cachedEntry = transcriptCacheRef.current.get(runId);
      if (!cachedEntry) return;
      inFlight = true;
      try {
        const transcript = await getAltYTranscript(runId, cachedEntry.lineCount, undefined, true);
        if (stopped || activeRunIdRef.current !== runId) return;
        const fetchedEvents = Array.isArray(transcript.events) ? transcript.events : [];
        const fromLine = typeof transcript.fromLine === 'number' && Number.isFinite(transcript.fromLine)
          ? Math.max(0, Math.floor(transcript.fromLine))
          : null;
        const lineCount = typeof transcript.lineCount === 'number' && Number.isFinite(transcript.lineCount)
          ? Math.max(0, Math.floor(transcript.lineCount))
          : cachedEntry.lineCount + fetchedEvents.length;
        if (!fetchedEvents.length || fromLine !== cachedEntry.lineCount) return;
        transcriptCacheRef.current.delete(runId);
        transcriptCacheRef.current.set(runId, {
          events: [...cachedEntry.events, ...fetchedEvents],
          lineCount,
          messages: messagesRef.current,
          updatedAt: Date.now(),
        });
        schedulePersistedChatCache();
        fetchedEvents.forEach((event) => handleEvent(event));
      } catch {
        /* ignore transient poll errors */
      } finally {
        inFlight = false;
      }
    }

    const timer = window.setInterval(tick, 3000);
    return () => {
      stopped = true;
      window.clearInterval(timer);
    };
  }, [busy, handleEvent, isOpen, schedulePersistedChatCache]);

  const addParticipant = useCallback(async (params: {
    type: 'user' | 'agent';
    id: string;
    label?: string;
    agentId?: string;
    mode?: string;
  }) => {
    const runId = activeRunIdRef.current;
    if (runId == null) return;
    try {
      const res = await addAltYParticipant(runId, params);
      if (res.participants) {
        setParticipants(res.participants);
      }
      return res.participant;
    } catch (err) {
      console.error('Failed to add participant', err);
      addMeta(`Failed to add participant: ${(err as Error).message || err}`);
      throw err;
    }
  }, [addMeta]);

  const sendHumanMessage = useCallback(async (
    text: string,
    recipientId?: string,
    recipientLabel?: string,
    images: AltYImageAttachment[] = [],
  ) => {
    const trimmed = text.trim();
    const attachments = Array.isArray(images) ? images : [];
    if (!trimmed && !attachments.length) return;
    const runId = activeRunIdRef.current;
    if (runId == null) return;

    try {
      const res = await addAltYHumanMessage(runId, {
        text: trimmed,
        recipientId,
        recipientLabel,
        images: attachments,
      });
      if (res.participants) {
        setParticipants(res.participants);
      }
      if (res.event) {
        recordLiveTranscriptEvent(res.event);
        handleEvent(res.event);
      }
      void refreshChats();
    } catch (err) {
      console.error('Failed to send human message', err);
      addMeta(`Failed to send human message: ${(err as Error).message || err}`);
    }
  }, [addMeta, handleEvent, recordLiveTranscriptEvent, refreshChats]);

  return {
    chats,
    chatGroups,
    refreshGroups,
    createGroup,
    renameGroup,
    removeGroup,
    moveChatToGroup,
    messages,
    activities,
    status,
    busy,
    thinking,
    connected,
    activeRunId,
    mode,
    queuedMessages,
    cancelQueued,
    switchMode,
    switchChatMode,
    sidebarExpanded,
    setSidebarExpanded,
    refreshChats,
    attachChat,
    startNewChat,
    startSubchat,
    removeChat,
    preserveEmptyChat,
    sendMessage,
    stop,
    stopChat,
    loadToolOutput,
    clearChatCache,
    copyTranscript,
    improveFromChat,
    improveAgent,
    deepImprove,
    promoteAiToTesting,
    promoteTestingToLive,
    editPinnedAgent,
    makeAgent,
    setPendingPrimer,
    setPendingAuto,
    renameChat,
    addMeta,
    devEnv,
    participants,
    currentUserEmail,
    addParticipant,
    sendHumanMessage,
    prefixEstimate,
  };
}

// The hook's return value — used as a prop type when chat state is lifted to
// App.tsx so the Agent Factory canvas can read the same messages the panel writes.
export type AltYChatApi = ReturnType<typeof useAltYChat>;
